
import React, { useState, useRef, useEffect, useCallback, useReducer, useMemo, act } from 'react';
import { useNavigate } from "react-router-dom";
import { ArrowDropDown, RestaurantMenu } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  IconButton,
  MenuItem,
  Button,
  Select,
  Autocomplete,
  TextField,
  InputAdornment,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import { DeleteForeverRounded } from '@mui/icons-material';
import AlertNotification from '../../AlertNotification';
import ToggleSplitPane from '../../ToggleSplitPane';
import CaseInformationPane from '../CaseInformation';
import AdditionalTaskQueueDialog from './AdditionalTaskQueueDialog'
import PageLoader from '../../PageLoader';
import AddNewWorkItemDialog from './AddNewWorkItemDialog';
import { MasterEnum } from "../../../enums/MasterEnum";

import {
  TextAreaWithCount,
  TextFieldWithIconButton,
  UtilityLibrary,
  ApiManager,
  DebounceForm,
  ValidateRequiredFieldsInForm,
  ValidateForm,
  sessionService,
  SessionManager,
  ConfirmationDialog,
  NotificationDialog,
  CaseSearchDialog,
  CommonDialog,
  EnumUtility,
  ObjectUtility,
  batchStatusModel,
  NotificationDialogModel,
  CaseDocumentsModel,
  NarrativeModel,
  DeterminePermissions,
  LoggerUtility,
  TimerUtility,
  ClientType,
  SharedService,
  CaseWorkflowService,
  ScrapeDataManager,
} from '../../ComponentsLibrary';

import { useNavigationContext } from '../../../functions/NavigationContext';
import { useConfigContext } from '../../../functions/ConfigContext';
import { useLocation } from 'react-router-dom';
import { ca } from 'date-fns/locale';

// temporary holiday list until we get a holiday api state specific call made
const holidays = [
  { date: "01-01", name: "New Year's Day" },
  { date: "01-15", name: "Martin Luther King Jr. Day" },
  { date: "02-19", name: "Presidents' Day" },
  { date: "05-27", name: "Memorial Day" },
  { date: "06-19", name: "Juneteenth" },
  { date: "07-04", name: "Independence Day" },
  { date: "09-02", name: "Labor Day" },
  { date: "10-14", name: "Columbus Day" },
  { date: "11-11", name: "Veterans Day" },
  { date: "11-28", name: "Thanksgiving Day" },
  { date: "12-25", name: "Christmas Day" }
];

const getToday = () => new Date();
const getNYDateToday = () => {
  const today = getToday();
  return new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
}

const getLoginStateID = () => {
  console.log("HERE State Id accessed")
  const loginUserInfo = sessionService.getUser();
  return loginUserInfo ? loginUserInfo.stateID : 0;
};


const FormFieldEnableType = {
  PageLoad: { ID: 100, name: "PageLoad" },
  NextWorkItemClick: { ID: 200, name: "NewWorkItemClick" },
  NoMoreTasks: { ID: 201, name: "NoMoreTasks" },
  Cancel: { ID: 202, name: "Cancel" },
  WorkflowSelect: { ID: 500, name: "WorkflowSelect" },
  ActionOptionSelect: { ID: 501, name: "ActionOptionSelect" },
  ActionOptionSelected: { ID: 20, Description: "Action option selected" },
  ActionOptionCleared: { ID: 21, Description: "Action option cleared" },
  RemoveFromActionList: { ID: 1000, name: "RemoveFromActionList" }
};

const FormActionType = {
  Save: { ID: 100, name: "Save" },
};

const CaseSearchSource = {
  CaseNumberField: { ID: 100, name: "CaseNumberField" },
  PreviousCaseField: { ID: 200, name: "PreviousCaseField" },
  CorrectCaseSearchField: {ID:300, name:"CorrectCaseSearchField"}
};

// Initial state for form fields
const initialState = {
  workflowSelect: false,
  actionOptionSelectedItem: false,
  nextActionTypeSelect: false,
  nextActionDueDateTxt: false,
  nextActionTypeReferActionSelect: false,
  addToListBtnEnabled: false,
  addNewItemBtnEnabled: false,
  noteForActionTxt: false,
  narrativeForStateTxt: false,
  saveBtnEnabled: false,
};

const DialogFormFieldEnableType = {
  Initial: { ID: 1, Description: "Initial state" },
  WorkflowSelect: { ID: 2, Description: "Workflow selected" },
  ActionTypeSelect: { ID: 3, Description: "Action type selected" },
  ICCSelect: { ID: 4, Description: "ICC selected" },
  Clear: { ID: 5, Description: "Clear/Reset state" }
};

const initialDialogState = {
  workflowEnabled: true,
  actionTypeEnabled: false,
  iccEnabled: false,
  addWorkItemButtonEnabled: false,
  currentStep: DialogFormFieldEnableType.Initial.ID
};


// Reducer to manage enable/disable state
function reducer(state, action) {
  switch (action.type) {
    case "SET_FIELD_STATE":
      return { ...state, [action.payload.field]: action.payload.isEnabled };
    case 'SET_BUTTON_VISIBILITY':
      return { ...state, [action.payload.field]: action.payload.isVisible };
    case "SET_STATE_VARIABLE":
      return {
        ...state,
        hasBatchLocked: action.payload,
      };
    case "SET_ACTION_BUTTONS_STATE":
      return {
        ...state,
        addToListBtnEnabled: action.payload.enabled,
        addNewItemBtnEnabled: false,
        saveBtnEnabled: action.payload.enabled
      };
    default:
      throw new Error("Unknown action type");
  }
};

const dialogFormReducer = (state, action) => {
  switch (action.type) {
    case DialogFormFieldEnableType.Initial.ID:
      return initialDialogState;

    case DialogFormFieldEnableType.WorkflowSelect.ID:
      return {
        ...state,
        actionTypeEnabled: true,
        iccEnabled: false,
        addWorkItemButtonEnabled: false,
        currentStep: DialogFormFieldEnableType.WorkflowSelect.ID
      };

    case DialogFormFieldEnableType.ActionTypeSelect.ID:
      return {
        ...state,
        iccEnabled: action.payload?.enableICC || false,
        addWorkItemButtonEnabled: !action.payload?.enableICC, // Enable if no ICC required
        currentStep: DialogFormFieldEnableType.ActionTypeSelect.ID
      };

    case DialogFormFieldEnableType.ICCSelect.ID:
      return {
        ...state,
        addWorkItemButtonEnabled: true,
        currentStep: DialogFormFieldEnableType.ICCSelect.ID
      };

    case DialogFormFieldEnableType.Clear.ID:
      return initialDialogState;

    default:
      return state;
  }
};

// Function to apply Form Fields Enable logic
function formFieldsEnableLogic(dispatch, logicType, config = {}) {
  switch (logicType) {
    case FormFieldEnableType.PageLoad.ID:
    case FormFieldEnableType.Cancel.ID:
    case FormFieldEnableType.NoMoreTasks.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionOptionSelectedItem", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateformValue", isEnabled: false } });
      // dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      // dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtnEnabled", isEnabled: false } });
      // Disable buttons when form is reset/cancelled
      dispatch({ type: "SET_ACTION_BUTTONS_STATE", payload: { enabled: false } });
      break;

    case FormFieldEnableType.NextWorkItemClick.ID:
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "actionOptionSelectedItem", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "nextActionDueDateformValue", isEnabled: true } });
      // dispatch({ type: "SET_FIELD_STATE", payload: { field: "iccSelect", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      //dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtn", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "saveBtnEnabled", isEnabled: false } });
      break;

    case FormFieldEnableType.ActionOptionSelected.ID:
      dispatch({ type: "SET_ACTION_BUTTONS_STATE", payload: { enabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: true } });
      break;

    case FormFieldEnableType.ActionOptionCleared.ID:
      dispatch({ type: "SET_ACTION_BUTTONS_STATE", payload: { enabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "noteForActionTxt", isEnabled: false } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "narrativeForStateTxt", isEnabled: false } });
      break;

    default:
      break;
  }
}

// Helper function to update form values
function updateArrayStateValues(arrayStateSetter, updatedValues) {
  arrayStateSetter((prevValues) => ({
    ...prevValues,
    ...updatedValues
  }));
};

const NextWorkitem = React.memo(({ }) => {
  useEffect(() => {
    document.title = 'YTrac :: Next Work Item';
  }, []); // Empty dependency array to set the title only on mount

  const navigate = useNavigate();
  const { setActivePage } = useNavigationContext();
  const { configLibrary } = useConfigContext();
  const location = useLocation();
  const { actionOptionsResults, actionOptionsCaseNumber } = location.state || {};

  useEffect(() => {
    if (actionOptionsResults && actionOptionsResults.length > 0) {
      handleNextWorkItemClick();

      // consumedInitialTaskRef.current = true; // Mark the incoming task as consumed
      // Prevent re-use on subsequent Next Work Item clicks
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [actionOptionsResults]);

  
  const leftPaneTitle = "Work Item";
  const rightPaneTitle = "Case Information";
  const errorFormName = "Next Work Item";

  const COMPLAINT_WORKFLOW = 141;
  const FOLLOWUPS_WORKFLOW = 145;

  // Create a ref for the child component
  const caseInfoRef = useRef(null);

  const masterServerDBSchema = SessionManager.GetDataFromSession("SiteSettingsDetailsKey")?.masterDBSchema;

  const currentDate = UtilityLibrary.formatDate(new Date().toISOString(), "MM/DD/YYYY");
  const initActionOption =
  {
    workflowDetailID: -123456,
    workflowDetailName: "",
    workflowStepID: -123456,
    initialWorkflowStepID: null,
    initialIntervalValue: null,
    initialIntervalUnitID: null,
    workflowDetailValue: 0,
    lastWorkflowDetailID: -123456,
    displayOrder: 0,
    workflowActionTypeID: -123456,
    workflowActionTypeName: "",
    waT_NoteTemplateID: null,
    terminateWorkflow: false,
    alertEnableAutomation: false,
    workflowUnitTypeName: "",
    workflowCallListTypeID: -123456,
    workflowCallListTypeName: "",
    wcL_WorkflowID: -123456,
    wcL_WorkflowName: "",
    wcL_FKM_DepartmentID: -123456,
    callWorkflowID: null,
    ticChoice: "",
    documentTypeID: null,
    qaDispositionTypeID: null,
    //enableIndex: 0 
  };
  const initWorkflow = { workflowID: -123456, workflowName: "", workflowDepartmentID: -123456, startWorkflowStepID: -123456, referWorkflowStepID: -123456, enableAddTabInputChannelChoice: false, enableWorkTabStart: false, w_NoteTemplateID: 0 };
  const initWorkflowStep = { workflowStepID: -123456, workflowStepName: "", workflowStepDepartmentID: -123456, wS_DepartmentName: "", workflowCallListTypeID: -123456, workflowCallListTypeName: "", stepPriority: 0 };
  const initActionTypeSelectedItem = { actionTypeID: -123456, workflowID: -123456, actionType: "", displayOrder: 0, initialWorkflowStepID: 0, workflowStartID: 0, workflowStartTypeID: 0, workflowMapID: 0 };
  const initICCSelect = { iccTypeID: -123456, workflowID: 0, dropdownName: "", workflowStartTypeID: 0, workflowStartID: 0, workflowStartOrder: 0 };
  const initSelectedNAT = { nextActionTypeID: -123456, workflowID: -123456, workflowStepID: -123456, workflowStepName: "", departmentName: "", displayOrder: 0 };

  const initialFormValues = {
    workflowSelect: initWorkflow,
    workflowItemSelect: initWorkflow,
    lastActionSelect: "",
    nextActionTypeSelect: initWorkflowStep,
    nextActionTypeReferActionSelect: initWorkflowStep,
    nextActionDueDateformValue: "",
    nextActionDueDateTxt: null,
    actionOptionSelectedItem: initActionOption,
    actionTypeSelectedItem: initActionTypeSelectedItem,
    iccSelect: initICCSelect,
    noteForActionTxt: "",
    narrativeForStateTxt: ""
  };

  const clearSaveFormValues = {
    workflowSelect: initWorkflow,
    workflowItemSelect: initWorkflow,
    lastActionSelect: "",
    nextActionTypeSelect: initWorkflowStep,
    nextActionTypeReferActionSelect: initWorkflowStep,
    nextActionDueDateformValue: "",
    nextActionDueDateTxt: null,
    actionOptionSelectedItem: initActionOption,
    actionTypeSelectedItem: initActionTypeSelectedItem,
    iccSelect: initICCSelect,
    noteForActionTxt: "",
    narrativeForStateTxt: ""
  };


  const [requiredFieldLabels, setRequiredFieldLabels] = useState({
    workflowSelect: "",
    workflowItemSelect: "",
    lastActionSelect: "",
    nextActionTypeSelect: "",
    nextActionDueDateformValue: "",
    nextActionDueDateTxt: "",
    actionOptionSelectedItem: "",
    actionTypeSelectedItem: "",
    nextActionTypeReferActionSelect: "",
    iccSelect: "",
    noteForActionTxt: "",
    narrativeForStateTxt: ""
  });

  const [formState, dispatch] = useReducer(reducer, initialState);
  const [formValues, setFormValues] = useState(initialFormValues);

  const [dialogFormState, dialogDispatch] = useReducer(dialogFormReducer, initialDialogState);

  const initialCaseNumberIconsDisabled = { search: false, copy: false, clear: false };
  const [caseNumberIconsDisabled, setCaseNumberIconsDisabled] = useState(initialCaseNumberIconsDisabled);

  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  const [caseValidationType, setCaseValidationType] = useState(EnumUtility.CaseType.Case);

  const [paginationModel, setPaginationModel] = useState({ pageSize: 5, page: 0 });
  const [initialSearch, setInitialSearch] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState({ items: [] });
  const [hasListOfActions, setHasListOfActions] = useState(true);

  const [listOfActionsResults, setListOfActionsResults] = useState([]);
  const [lastAddedItemId, setLastAddedItemId] = useState(null);
  const [nextItemId, setNextItemId] = useState(2);
  const ADD_TO_LIST_ITEM_ID = 1; // Reserved — only the AddToList row uses this id

  const [caseInfoData, setCaseInfoData] = useState(null);
  const [mapAdditionalTasksData, setMapAdditionalTasksData] = useState([]);

  const [newCaseWorkflowsList, setNewCaseWorkflowsList] = useState([]);
  const [pendingDuplicateSubmit, setPendingDuplicateSubmit] = useState(null);
  const actionStartTimeRef = useRef(null);
  const newWorkItemStartTimeRef = useRef(null);
  const consumedInitialTaskRef = useRef(false); // Get Next Task from the incoming actionOptionsResults only once, then clear it to prevent re-use on subsequent Next Work Item clicks

  // const [searchCasePopUpScheduled, setSearchCasePopUpScheduled] = useState(false);
  // const [searchCasePopUpDisplayed, setSearchCasePopUpDisplayed = useState(false);
  const [caseSearchDialogOpen, setCaseSearchDialogOpen] = useState(false);
  const [correctCaseSearchData, setCorrectCaseSearchData] = useState(null);
  const [correctCaseSearchComplete, setCorrectCaseSearchComplete] = useState(false);
  const [indexCase, setIndexCase] = useState(false);
  const [selectedDocGroupID, setSelectedDocGroupID] = useState(null);
  const [selectedDocTypeID, setSelectedDocTypeID] = useState(null);
  const [documentSource, setDocumentSource] = useState(null);

  useEffect(() => {
    console.log("CASE SEARCH DIALOG", caseSearchDialogOpen)
  }, [caseSearchDialogOpen]); // Empty dependency array to set the title only on mount


  const getUserLoginInfo = () => {
    const loginUserInfo = sessionService.getUser();
    const loginLegacyUserID = loginUserInfo ? loginUserInfo.legacyUserID : 0;
    const loginSiteID = loginUserInfo ? loginUserInfo.siteID : 0;
    const loginStateID = loginUserInfo ? loginUserInfo.stateID : 0;
    const currentDateTime = new Date().toISOString();
    const timeZone = loginUserInfo ? loginUserInfo.timeZone : "";

    return { loginLegacyUserID, loginSiteID, loginStateID, currentDateTime, timeZone };
    
  };

  const captureActionStartTime = () => {
    const { timeZone } = getUserLoginInfo();
    actionStartTimeRef.current = UtilityLibrary.getCurrentDateTimeForTimeZone(timeZone);
  };

  // Data for the document grid in view 2
  const listOfActionsGridColumns = [
    {
      field: 'action', headerName: 'Action', flex: 1,
      renderCell: (params) => {
        return (
          <div className="d-flex justify-content-start align-items-center" style={{ cursor: "pointer" }}>
            <DeleteForeverRounded index={params.row.id}
              onClick={() => handleRemovActionConfirmationDialog(params.row.id)}
              color='ywMainBlue' />
            <div style={{ paddingLeft: "10px" }}>
              {params.row.action}
            </div>
          </div>
        );
      }
    },
    { field: 'workflow', headerName: 'Workflow', flex: 1 },
    { field: 'queue', headerName: 'Queue', flex: 1 },
    { field: 'daysToNext', headerName: 'Days to Next', flex: 1 },
    { field: 'nextActionType', headerName: 'Next Action Type', flex: 1 },
  ];


  
  const getCaseValidationRules = (values) => {
    return {
      actionOptionSelectedItem: {
        required: true,
        validator: (value) =>
          value &&
          value.workflowDetailID &&
          value.workflowDetailID !== initActionOption.workflowDetailID
      },
      nextActionDueDateformValue: {
        required: true,
        validator: (value) => String(value || "").trim() !== ""
      },
      noteForActionTxt: { required: false },
      narrativeForStateTxt: {
        required: true,
        validator: (value) => String(value || "").trim() !== ""
      }
    };
  };
  
  const getNoCaseValidationRules = (values) => {
    return {
      actionOptionSelectedItem: {
        required: true,
        validator: (value) =>
          value &&
        value.workflowDetailID &&
        value.workflowDetailID !== initActionOption.workflowDetailID
      },
      nextActionDueDateformValue: {
        required: true,
        validator: (value) => String(value || "").trim() !== ""
      },
      noteForActionTxt: {
        required: true,
        validator: (value) => String(value || "").trim() !== ""
      },
      narrativeForStateTxt: { required: false }
    };
  };
  
  
  function getValidationRules(values, validationType) {
    switch (validationType) {
      case EnumUtility.CaseType.Case:
        return getCaseValidationRules(values);

      case EnumUtility.CaseType.NoCase:
        return getNoCaseValidationRules(values);

      default:
        return getCaseValidationRules(values);
    }
  };

  // Debounced validation using the centralized validateForm
  const debouncedValidation = useMemo(
    () =>
      DebounceForm((values, validationType, currentFormState) => {
        try {
          const rules = getValidationRules(values, validationType) || {};

          const validationResults = ValidateRequiredFieldsInForm(
            values,
            rules,
            currentFormState
          );

          setRequiredFieldLabels({
            workflowSelect: validationResults.validationLabels.workflowSelect || "",
            lastActionSelect: validationResults.validationLabels.lastActionSelect || "",
            nextActionTypeSelect:
              validationResults.validationLabels.nextActionTypeSelect || "",
            nextActionDueDateformValue:
              validationResults.validationLabels.nextActionDueDateformValue || "",
            actionOptionSelectedItem:
              validationResults.validationLabels.actionOptionSelectedItem || "",
            noteForActionTxt:
              validationResults.validationLabels.noteForActionTxt || "",
            narrativeForStateTxt:
              validationResults.validationLabels.narrativeForStateTxt || ""
          });

          setIsSaveEnabled(Boolean(validationResults.isFormValid));
        }
        catch (error) {
          throw LoggerUtility.ProcessErrorData(error, "debouncedValidation", {});
        }
      }, 300),
    []
  );

  useEffect(() => {
    debouncedValidation(formValues, caseValidationType, formState);
  }, [formValues, caseValidationType, formState, debouncedValidation]);

  const [isEditable, setIsEditable] = useState(false);

  const [showExtDocGrid, setShowExtDocGrid] = useState(false);
  const [holidayList, setHolidayList] = useState([])

  const caseNumberRef = useRef();

  // Validation patterns
  const caseNumberRegex = JSON.parse(SessionManager.GetValueFromSession(SessionManager.CaseNumberRegexKey)) || { caseRegexValue: "", caseRegexMessage: "" };

  // Narrative Character Limit
  const narrativeCharLimit = SessionManager.GetValueFromSession(SessionManager.NarrativeCharsLimitKey) || 500;

  // Notes Character Limit
  const notesCharLimit = SessionManager.GetValueFromSession(SessionManager.NotesCharsLimitKey) || 500;

  const siteSettingDetail = JSON.parse(SessionManager.GetValueFromSession(SessionManager.SiteSettingsDetailsKey));

  const [externalBatchID, setExtBatchID] = useState(0);

  const [leftPaneMessage, setLeftPaneMessage] = useState("");

  const [isPageLoading, setIsPageLoading] = useState(false);

  const noteForActionRef = useRef();
  const narrativeForStateRef = useRef();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmationDialogContent, setConfirmationDialogContent] = useState([]);

  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationDialogContent, setNotificationDialogContent] = useState([]);
  const [timerNotificationDialogClose, setTimerNotificationDialogClose] = useState(5000);
  const [timerAutoCloseNotificationDialog, setTimerAutoCloseNotificationDialog] = useState(false);

  const [shouldFocusCaseSearch, setShouldFocusCaseSearch] = useState(false);

  const [actionOptionsState, setActionOptionsState] = useState([]);

  const [nextActionTypeReferAction, setNextActionTypeReferAction] = useState([]);
  const [showNATReferAction, setShowNATReferAction] = useState(false);

  const [noCase, setNoCase] = useState(false);
  const [currentCaseWorkflowID, setCurrentCaseWorkflowID] = useState(-123456);
  const [currentCaseNumber, setCurrentCaseNumber] = useState("");

  const currentCaseWorkflowIDRef = useRef(-123456);

  const defaultPermissionsForFields = {
    nextWorkItemBtn: { read: false, write: true, hide: false },
    noteForActionTxt: { read: false, write: true, hide: false },
    narrativeForStateTxt: { read: false, write: true, hide: false },
    cancelBtn: { read: false, write: true, hide: false },
    leftPaneMessageLbl: { read: false, write: true, hide: false },
    saveBtn: { read: false, write: true, hide: false },
  };
  const [formFieldAccess, setFormFieldAccess] = useState(defaultPermissionsForFields);

  const [timerView, setTimerView] = useState("00:00");
  const timerRef = React.useRef(null); // Keep the TimerUtils instance
  const isTimerVisible = false;

  // Initialize timer only once
  if (!timerRef.current) {
    timerRef.current = new TimerUtility(setTimerView);
  };

  const [workflowItems, setWorkflowItems] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [iccItems, setIccItems] = useState([]);
  const [addWorkItemNAT, setAddWorkItemNAT] = useState(initSelectedNAT);
  const [iccEnabled, setIccEnabled] = useState(false);

  const [dialogCommonOpen, setDialogCommonOpen] = useState(false);
  const DialogContentType = {
    AddWorkItem: "AddNewWorkItem",
    AdditionalTaskQueue: "AdditionalTaskQueue"
  };
  const [dialogCommonState, setDialogCommonState] = useState({
    openDialog: false,
    contentType: null, // 'search', 'form', 'preview', etc.
    contentProps: {}
  });

  const toUpperCase = (stringToUpperCase) => {
    return String(stringToUpperCase).toUpperCase();
  };


  const [alertProps, setAlertProps] = useState({
    type: '',
    message: '',
    open: false,
  });

  const showAlert = (type, message) => {
    setAlertProps({ type, message, open: true });
  };


  useEffect(() => {
    const getHolidayList = async () => {
      const loginUserInfo = sessionService.getUser();
      const { siteID } = loginUserInfo || {};
      const params = { siteID }
      const holidays = await ApiManager.getHolidayList(params)
      const updatedHolidays = holidays.map((holiday) => {
        const date = new Date(holiday?.currentDate)
        return new Date(date.getFullYear(),
          date.getMonth(),
          date.getDate())
      })
      setHolidayList(updatedHolidays)
    }
    (async () => await (getHolidayList()))();
  }, [])


  // Update both state and ref when setting the CaseWorkflowID
  const setCurrentCaseWorkflowIDWithRef = (id) => {
    setCurrentCaseWorkflowID(id);
    currentCaseWorkflowIDRef.current = id;
  };

  const lockUnlockByCaseWorkflow = async (lockValue, CWID = 0) => {
    try {
      const loginUserInfo = sessionService.getUser();
      const caseWorkflowID = CWID || currentCaseWorkflowIDRef.current;

      // Guard: Don't attempt lock/unlock with invalid workflow ID
      if (!caseWorkflowID || caseWorkflowID <= 0) {
        console.log(`Skipping lock/unlock: invalid caseWorkflowID (${caseWorkflowID})`);
        return null;
      }

      // lockValue: 1=Lock, 0=Unlock
      const result = await CaseWorkflowService.lockOrUnLockCaseWorkflow(
        caseWorkflowID,
        lockValue,
        loginUserInfo?.legacyUserID,
        masterServerDBSchema
      );

      console.log(`Lock or Unlock: ${lockValue === 1 ? 'LOCKED' : 'UNLOCKED'} (CWID: ${caseWorkflowID})`, result);

      // Validate response structure
      if (!result) {
        console.warn(`No response from lock/unlock operation for CWID: ${caseWorkflowID}`);
      }

      return result;
    }
    catch (error) {
      console.error(`Lock/Unlock error for CWID ${CWID}:`, error);
      throw LoggerUtility.ProcessErrorData(error, "lockUnlockByCaseWorkflow", {});
    }
  };

  // Cleanup function to clear locks or any other necessary cleanup when the component unmounts
  useEffect(() => {
    return () => {
      console.log("unlock caseworkflow");
      (async () => await lockUnlockByCaseWorkflow(0, currentCaseWorkflowIDRef.current))(); // Unlock the case workflow when component unmounts
    };
  }, []);

  //=======================================================================
  // Tabs Feature - Begin
  //=======================================================================
  function isUserAllowedNextActionDue() {
    const loginUserInfo = sessionService.getUser();
    const allowedRoleIDs = [1, 2, 3]; // IT Admin, Supervisor, Lead
    const userRoleID =
      loginUserInfo && typeof loginUserInfo.roleID === "number"
        ? loginUserInfo.roleID
        : -1;

    return allowedRoleIDs.includes(userRoleID);
  }



  function handleNextActionDueDateChange(selectedDate) {
    if (!selectedDate || isNaN(selectedDate.getTime())) {
      return;
    }

    // Build YYYY-MM-DD in LOCAL time (no UTC conversion)
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;

    setFormValues((prev) => {
      if (prev.nextActionDueDateTxt === formattedDate) {
        return prev;
      }

      // Validate past date
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const selected = new Date(year, Number(month) - 1, day);
      if (selected < today) {
        showAlert("error", "You cannot select a past date.");
        return prev;
      }

      // Validate weekend
      const dayOfWeek = selected.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        showAlert("error", "You cannot select a weekend date.");
        return prev;
      }
      return {
        ...prev,
        nextActionDueDateTxt: formattedDate,
      };
    });
  }

  function handleNextActionDueDateChangeClear() {
    setFormValues((prev) => ({ ...prev, nextActionDueDateTxt: null }));
  }

  const ymdToLocalIso = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d).toISOString();
  };

  // Function to handle Next Work Item Click
  const handleNextWorkItemClick = async () => {
    setIsPageLoading(true);
    try {
      setIsEditable(true);

      const { loginLegacyUserID, loginSiteID, loginStateID, currentDateTime, timeZone } = getUserLoginInfo();

      const params = {
        userLoginID: loginLegacyUserID,
        HighestSiteID: loginStateID,
        BoardedSiteID: loginSiteID,
        ModuleID: 1,
        masterServerDBSchema: masterServerDBSchema,
        WorkflowStateLockoutTimer: 60, //ToDo: Set the SessionKey SiteSettingsDetailsKey with LockTimer and fetch value
      };

      const useIncomingTask = !consumedInitialTaskRef.current && actionOptionsResults && actionOptionsResults.length > 0;
      // Queue tab has provided a task, so use it instead of making an API call to get the next task
      if (useIncomingTask) {
        consumedInitialTaskRef.current = true; // Mark the incoming task as consumed
        const caseNumber = actionOptionsCaseNumber; //nextTask?.caseNumber;
        const caseWorkflowID = actionOptionsResults[0]?.activeWorkflows[0]?.caseWorkflowID;//nextTask?.caseWorkflowID;
        setCurrentCaseWorkflowIDWithRef(caseWorkflowID);
        setCurrentCaseNumber(actionOptionsCaseNumber);

        lockUnlockByCaseWorkflow(1, caseWorkflowID); // Lock the case workflow when a new task is retrieved from Queue Tab

        const isNoCase = (caseNumber === 'PLACEHOLDER' || actionOptionsResults[0]?.activeWorkflows[0]?.yTracCaseID === 0);
        setNoCase(isNoCase);
        setCaseValidationType(isNoCase ? EnumUtility.CaseType.NoCase : EnumUtility.CaseType.Case);

        await onCaseSearch(caseNumber, caseWorkflowID, CaseSearchSource.CaseNumberField.ID, isNoCase);

        const activeWorkflowAction = actionOptionsResults[0]?.activeWorkflows || [];
        const currentAction = activeWorkflowAction[0] || {};

        populateUIandUpdateStateWithWorkflowInfo(currentAction);
      }
      else {
        // Make the API call to get the next workflow task
        const apiResponse = await ApiManager.WorkflowGetNextTask(params);

        // Process the API response as needed
        if (apiResponse && apiResponse[0].length > 0) {
          // Handle successful response
          console.log('Next task retrieved:', apiResponse);
          const nextTaskList = apiResponse[0];
          const nextTask = nextTaskList[0];
          const caseNumber = nextTask?.caseNumber;
          const caseWorkflowID = nextTask?.caseWorkflowID;

          setCurrentCaseWorkflowIDWithRef(caseWorkflowID);
          setCurrentCaseNumber(caseNumber);

          const isNoCase = (caseNumber === 'PLACEHOLDER' || nextTask.yTracCaseID === 0);
          setNoCase(isNoCase);
          setCaseValidationType(isNoCase ? EnumUtility.CaseType.NoCase : EnumUtility.CaseType.Case);

          await onCaseSearch(caseNumber, caseWorkflowID, CaseSearchSource.CaseNumberField.ID, isNoCase);

          const activeWorkflowAction = apiResponse[1] || [];
          const currentAction = activeWorkflowAction[0] || {};

          populateUIandUpdateStateWithWorkflowInfo(currentAction);

        } else {
          // Handle case when no tasks are returned
          console.log('No next task available');
          const dialogContent = {
            ...EnumUtility.NotificationAlerts.NoMoreTasksDueToday,
          };

          handleNotificationDialog(dialogContent);

          await cancelAndDisableForm();
          formFieldsEnableLogic(dispatch, FormFieldEnableType.NoMoreTasks.ID);
        }
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNextWorkItemClick");
    }
    finally { setIsPageLoading(false); }
  };

  const populateUIandUpdateStateWithWorkflowInfo = (data) => {
    try {
      captureActionStartTime();

      const workflowObj = data?.workflow;
      const workflowStepObj = data?.workflowStep;

      const selectedWorkflowValue = workflowObj
        ? {
          workflowID: workflowObj.workflowID,
          workflowName: workflowObj.workflowName,
          workflowDepartmentID: workflowObj.workflowDepartmentID,
          startWorkflowStepID: workflowObj.startWorkflowStepID,
          referWorkflowStepID: workflowObj.referWorkflowStepID
        }
        : { ...initWorkflow };

      const lastActionNameValue = data?.lwD_WorkflowDetailName || "";

      const nextDueDate = data?.nextTask?.nextStepDueDate;
      const nextDueDateToSet = (nextDueDate && nextDueDate !== null && nextDueDate !== "") ? UtilityLibrary.formatDate(nextDueDate, "MM/DD/YYYY") : currentDate;

      const selectedWorkflowStepValue = workflowStepObj
        ? {
          workflowStepID: workflowStepObj.workflowStepID,
          workflowStepName: workflowStepObj.workflowStepName,
          workflowStepDepartmentID: workflowStepObj.workflowStepDepartmentID,
          wS_DepartmentName: workflowStepObj.wS_DepartmentName,
          workflowCallListTypeID: workflowStepObj.workflowCallListTypeID,
          workflowCallListTypeName: workflowStepObj.workflowCallListTypeName,
          stepPriority: workflowStepObj.stepPriority
        }
        : { ...initWorkflowStep };

      const workflowDetails = (data.workflowDependency || []).map((action) => ({
        lastWorkflowDetailID: action.lastWorkflowDetailID,
        workflowDetailID: action.workflowDetailID,
        workflowDetailName: action.workflowDetailName,
        workflowStepID: action.workflowStepID,
        displayOrder: action.displayOrder,
        ticChoice: action.ticChoice,
        initialWorkflowStepID: action.initialWorkflowStepID,
        initialIntervalValue: action.initialIntervalValue,
        initialIntervalUnitID: action.initialIntervalUnitID,
        workflowDetailValue: action.workflowDetailValue,
        callWorkflowID: action.callWorkflowID,
        enableIndex: action.enableIndex,
        enableCaseSearch: action.enableSearchCase,
        workflowActionTypeID: action.workflowActionTypeID,
        workflowActionTypeName: action.workflowActionTypeName,
        waT_NoteTemplateID: action.waT_NoteTemplateID,
        enableIndexing: action.enableIndexing,
        terminateWorkflow: action.terminateWorkflow,
        alertEnableAutomation: action.alertEnableAutomation,
        workflowUnitTypeID: action.workflowUnitTypeID,
        documentTypeID: action.documentTypeID,
        qaDispositionTypeID: action.qaDispositionTypeID,
        workflowUnitTypeName: action.workflowUnitTypeName,
        workflowCallListTypeID: action.workflowCallListTypeID,
        workflowCallListTypeName: action.workflowCallListTypeName,
        wcL_WorkflowID: action.wcL_WorkflowID,
        wcL_WorkflowName: action.wcL_WorkflowName,
        wcL_FKM_DepartmentID: action.wcL_FKM_DepartmentID,
      }));

      setActionOptionsState(workflowDetails || []);

      const updatedFormValues = {
        ...initialFormValues,
        lastActionSelect: lastActionNameValue,
        nextActionTypeSelect: selectedWorkflowStepValue || { ...initWorkflowStep },
        workflowSelect: selectedWorkflowValue || { ...initWorkflow },
        actionOptionSelectedItem: { ...initActionOption },
        nextActionDueDateformValue: nextDueDateToSet,
      };

      formFieldsEnableLogic(dispatch, FormFieldEnableType.NextWorkItemClick.ID);
      setFormValues(updatedFormValues);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "populateUIandUpdateStateWithWorkflowInfo", {});
    }
  };

  const clearCaseTabInfo = async () => {
    if (caseInfoRef.current) {
      // Call the child method with parameters
      await caseInfoRef.current.clearCaseInfoFromExternal(externalBatchID, showExtDocGrid);
    }
  };


  const onCaseSearch = async (caseNumber, caseWorkflowID, caseSearchSource, isNoCase = false) => {
    try {
      if (caseInfoRef.current) {
        // Call the child method with parameters
        let caseID;

        if (!isNoCase) {
          caseID = toUpperCase(caseNumber);
        } else {
          caseID = caseWorkflowID;
        }

        await caseInfoRef.current.onSelectCaseFromExternal(caseID, showExtDocGrid, caseSearchSource, isNoCase, caseWorkflowID);
      }
    }
    catch (error) {
      await LoggerUtility.ProcessErrorData(error, "onCaseSearch", {});
    }
  };

  // Function to handle action option change
  const handleActionOptionChange = async (e, newValue, reason) => {
    try {
      const selectedAction = reason === "clear" || !newValue ? { ...initActionOption } : { ...newValue };

      const isValidAction = selectedAction.workflowDetailID && selectedAction.workflowDetailID !== initActionOption.workflowDetailID;
      console.log("OPTIONS ", actionOptionsState, selectedAction)

      if (isValidAction) {
        const loginUserInfo = sessionService.getUser();
        const { siteID, stateID } = loginUserInfo || {};
        if (selectedAction.workflowDetailID < 0) {
          // Negative IDs (other than -123456) are refer-action types → show NAT section
          setShowNATReferAction(true);
          await fetchNATReferAction(selectedAction.workflowDetailID);
        } else {
          // Positive IDs are normal actions → hide NAT section
          setShowNATReferAction(false);
          setNextActionTypeReferAction([]);
        }
        formFieldsEnableLogic(dispatch, FormFieldEnableType.ActionOptionSelected.ID);

        const shouldMarkWorkedDocIden = selectedAction.enableIndex === true;
        setIsWorkedDocIden(shouldMarkWorkedDocIden);
        isWorkedDocIdenRef.current = shouldMarkWorkedDocIden;

        //TODO check the sequence
        //                else if (Y_Trac_Common.Client.ClientControls.UserDMS()?.IntegrationTypeID == (int)Enum_MasterType.IntegrationType.Atalasoft)
        if (selectedAction.enableIndex === true) {
          const relatedDocuments = await ApiManager.GetWorkflowDocuments({ caseworkflowID: currentCaseWorkflowIDRef.current, caseNumber: "" })
          const document = await ApiManager.GetCaseDocsFromApiByCaseID({ documentID: relatedDocuments?.[0]?.[0]?.batchDocumentID, caseNumber: null });
          const documentType = document?.[0]?.documentTypeName;
          setDocumentSource(document?.[0]?.documentSource);
          const result = await ApiManager.GetSettingsDetailValuesFromApi({
            settingsDetailIDList: String(MasterEnum.SettingsDetail.Workflow_Parameters__DocumentTypeID_Supervisor),
            yTracSiteID: siteID
          });
          const supeRequiredDocTypeID = result?.settingDetailsApiData?.[0]?.settingsDetailValue
          const documentTypes = await ApiManager.GetDocTypesFromApi({ clientID: siteID, documentTypeID: Number(supeRequiredDocTypeID) })
          const isSupRequiredBool = (documentType === documentTypes?.[0]?.documentTypeName);
          setIsSupRequired(isSupRequiredBool);
          console.log("isWorkedDocIden=====", shouldMarkWorkedDocIden);
        }
        
        setNoteTemplateID(selectedAction.waT_NoteTemplateID);    // action-specific mainframe narrative 
        ////const ReferToWorkflowID = _CaseWorkflowService.getReferWorkflowID(value);
        
        if (selectedAction.enableCaseSearch === true && caseSearchDialogOpen === false)
        {
          if (noCase || indexCase)
          {
            setCaseSearchDialogOpen(true);
          }
        }
      } else {
        setShowNATReferAction(false);
        setNextActionTypeReferAction([]);
        formFieldsEnableLogic(dispatch, FormFieldEnableType.ActionOptionCleared.ID);
      }

      setFormValues(prev => ({
        ...prev,
        actionOptionSelectedItem: selectedAction
      }));
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleActionOptionChange");
    }
  };


  const handleOkClick = async () => {
    await cancelAndDisableForm();//passed from parent
  };

  // Function to handle case search completion
  const handleCaseSearchComplete = async (hasValidCase, caseSearchInfo, caseSearchSource) => {
    try {
      if (noCase && caseSearchSource !== CaseSearchSource.CorrectCaseSearchField.ID) return; // If it's a no-case scenario, skip processing case search completion

      setIsPageLoading(false);
      // let updatedFormValues = formValues;
      if (hasValidCase && caseSearchSource === CaseSearchSource.CorrectCaseSearchField.ID) setCorrectCaseSearchComplete(true);

      const caseInfo = caseSearchInfo.caseData ?? null;
      (caseSearchSource === CaseSearchSource.CorrectCaseSearchField.ID) ? setCorrectCaseSearchData(caseInfo): setCaseInfoData(caseInfo)
      let dialogContent;
      const loginUserInfo = sessionService.getUser();
      if (caseSearchInfo.statusCode === EnumUtility.CaseSearchResultCode.CaseNotFound.ID) {
        const errorMessage = { ID: 321, Name: "NetworkError", NotificationType: EnumUtility.NotificationType.Warning, Title: "Warning", Message: caseSearchInfo.errorMessage };

        const dialogContent = {
          ...errorMessage,
          dynamicMessageContent: "",
        };

        handleNotificationDialog(dialogContent);
        if ( caseSearchSource !== caseSearchSource.CorrectCaseSearchField ) await cancelAndDisableForm();
      }
      else if (!hasValidCase && caseSearchInfo.apiError) {
        handleNotificationDialog({
          NotificationType: EnumUtility.NotificationType.Warning,
          Title: "Warning",
          Message: caseSearchInfo.errorMessage
        });
      }
      else {
        if (caseSearchInfo?.isCaseConfidential && caseSearchInfo?.isConfidentialForThisUser) {
          // If the case was marked confidential for THIS USER then show the message below.
          const dialogContent = await CaseWorkflowService.createEscalationNotificationMessage(caseSearchInfo?.caseData, loginUserInfo, masterServerDBSchema)
          handleNotificationDialog(dialogContent);
        }
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseSearchComplete");
    }
  };

  const changeCaseNumberIconsDisable = (iconsEnabledValue) => {// RC removed async as it not needed
    try {
      const updatedCaseNumberIconsEnabled = {
        ...caseNumberIconsDisabled,
        search: iconsEnabledValue.search,
        copy: iconsEnabledValue.copy,
        clear: iconsEnabledValue.clear,
      };
      updateArrayStateValues(setCaseNumberIconsDisabled, updatedCaseNumberIconsEnabled);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "changeCaseNumberIconsDisable", {});
    }
  };

  const checkCaseNumberRegEx = async (caseNumber) => {// RC removed async as its not needed
    try {
      let isValidFormat = true;
      const caseNumberRegexValue = new RegExp(caseNumberRegex.caseRegexValue || "");

      if ((caseNumberRegexValue !== "") && (caseNumber !== "")) {
        if (!caseNumberRegexValue.test(caseNumber)) {
          caseNumberRef.current.setValidationMessage(caseNumberRegex.caseRegexMessage);
          changeCaseNumberIconsDisable({ search: true, copy: false, clear: false });
          isValidFormat = false;
        }
        else {
          caseNumberRef.current.setValidationMessage("");
          changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
        }
      }
      else {
        caseNumberRef.current.setValidationMessage("");
        changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
      }

      return isValidFormat;
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "checkCaseNumberRegEx", {});
    }
  };

  const [correctCaseSearch, setCorrectCaseSearch] = useState();
  const [waT_noteTemplateID, setNoteTemplateID] = useState();
  const [isSupRequired, setIsSupRequired] = useState(false);
  const [isWorkedDocIden, setIsWorkedDocIden] = useState(false);
  const isWorkedDocIdenRef = useRef(false);
  const [documentGroups, setDocumentGroups] = useState([]);

  useEffect(() => {
    isWorkedDocIdenRef.current = isWorkedDocIden;
  }, [isWorkedDocIden]);

  const handleCorrectCaseChange = async (caseNumberTxt) => {
    try {
      setCorrectCaseSearch(caseNumberTxt);
      setCorrectCaseSearchComplete(false);
      if (caseNumberTxt === "") {
        setCorrectCaseSearchData(null);
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseChange");
    }
  };

  const handleCorrectCaseSearchClear = async () => {
    setCorrectCaseSearchData(null);
    setCorrectCaseSearchComplete(false)
}
  

  // Function to handle search
  const handleCorrectCaseSearch = async () => {
    try {
      await simulateAsyncDelay();
      setIsPageLoading(true);
      await caseInfoRef.current.fetchCaseDataOnly(correctCaseSearch, CaseSearchSource.CorrectCaseSearchField.ID);
      }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseSearch");
    }
    // finally { setIsPageLoading(false); }
  };

  const handleApplyCaseClick = async (caseNumber, selectedDocGroup = null, selectedDocType = null) => {
    
    // IndexCase = true;
    // CaseAttached = true;
    // CancelIndexCase = false;
    // ClientEntities.selectedCpSSN = obj.CPSSN?.Trim();
    // ClientEntities.selectedNcpSSN = obj.NCPSSN?.Trim();
    //TOODO maybe add a check to see if case number is not empty
    setSelectedDocGroupID(selectedDocGroup?.id ?? null);
    setSelectedDocTypeID(selectedDocType?.id ?? null);
      // UpdateDocGroupAndDocType();
    setIndexCase(true);
    setNoCase(false);
    setCaseValidationType(EnumUtility.CaseType.Case);
    setCurrentCaseNumber(correctCaseSearchData?.caseNumber);
    setCaseInfoData(correctCaseSearchData);

    await onCaseSearch(correctCaseSearchData?.caseNumber,
      CaseSearchSource.CaseNumberField.ID, false);
    
    setIsSupRequired(false);
    setCaseSearchDialogOpen(false);
    setCorrectCaseSearch('');
    setCorrectCaseSearchData(null);
    setCorrectCaseSearchComplete(false);
    
    //TODO change caseworkflowid

  // ResetToOriginalCaseNumber = CaseNumber;

  // IsNarrativeEnabled = true;
  }


  const handleProceedWithoutCaseClick = async(selectedDocGroup = null, selectedDocType = null) => {
    //reset case number
    //
     if ((isSupRequired === true && selectedDocType != null) || (isSupRequired === false))
    {
      // UpdateDocGroupAndDocType();
    } 
    setIndexCase(false);
    setCaseSearchDialogOpen(false);
    setIsSupRequired(false);
    setCurrentCaseNumber('PLACEHOLDER');
    setNoCase(true);
    setCaseValidationType(EnumUtility.CaseType.NoCase);

    await clearCaseTabInfo();

    await onCaseSearch('PLACEHOLDER',
      currentCaseWorkflowIDRef.current,
      CaseSearchSource.CaseNumberField.ID, true);

    setCaseInfoData(null);
  }


  //=======================================================================
  // Tabs Feature - End
  //=======================================================================


  const simulateAsyncDelay = async () => {
    await new Promise((resolve) => setTimeout(resolve, 0));
  };

  //aa--------------------------------------------------------------------
  /* 
  * Authorization code starts here
  * The permission values for fields are received from Web API
  * The received data from Web API response is processed and stored in a state variable "formFieldAccess"
  */
  useEffect(() => {
    const fetchFormFieldPermissions = async () => {
      try {
        //TODO: Pass variables from session to the below URL
        const loginUserInfo = sessionService.getUser();
        const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
        const params = {
          clientID: loginStateID,
          formID: 500      // To-do: Modify Code - Hard-coded value of FormID: 1
        }
        const controlSecurityListApiData = await ApiManager.FetchControlsPermissions(params);
        const actualPermissionsForFields = DeterminePermissions(controlSecurityListApiData);
        setFormFieldAccess({ ...defaultPermissionsForFields, ...actualPermissionsForFields });
      }
      catch (error) {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchFormFieldPermissions");
      }
    };

    fetchFormFieldPermissions();
  }, []);

  const showNarrativeUpdateFailure = (apiErrorMessage) => {
    handleNotificationDialog({
      NotificationType: EnumUtility.NotificationType.Error,
      Title: 'Error',
      Message: apiErrorMessage || 'Failed to update narrative. Check the scraper app and the mainframe.'
    });
  };

  const stripLeadingNarrativeSeparator = (text) => (text || "").replace(/^\s*\|\s*/, "");

  const clearFormStateValues = () => {
    try {
      actionStartTimeRef.current = null;
      newWorkItemStartTimeRef.current = null;
      setFormValues((prevValues) => ({
        ...prevValues,
        ...initialFormValues
      }));


    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearFormStateValues", {});
    }
  };

  const formValuesRef = useRef(formValues);
  useEffect(() => { formValuesRef.current = formValues; }, [formValues]);


  const handleRemovActionConfirmationDialog = (primaryID) => {
    // Remove the item with matching ID from the results
    setListOfActionsResults(prevResults =>
      prevResults.filter(item => item.id !== primaryID)
    );

    setNewCaseWorkflowsList(prev => prev.filter(item => item.newId !== primaryID));  //remove caseworkflow from the list of new case workflows

    if (lastAddedItemId === primaryID) {
      setLastAddedItemId(null); // Clear tracking
      setFormValues(prevValues => ({
        ...prevValues,
        actionOptionSelectedItem: initActionOption,
        nextActionTypeReferActionSelect: initWorkflowStep,
        nextActionDueDateTxt: null
      }));

      setShowNATReferAction(false);
      setNextActionTypeReferAction([]);
      noteForActionRef.current?.clear();
      // narrativeForStateRef.current?.clear();
      console.log("Re-enabled ADD TO LIST button and cleared action option after removing item ID::", primaryID);
    }
  };

  const handleCancelClick = async () => {
    try {
      if (UtilityLibrary.CompareTwoObjects(formValues, initialFormValues)) {
        await cancelAndDisableForm();
      }
      else {
        handleConfirmationDialog(EnumUtility.ConfirmDialogType.NewDocumentCancel);
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCancelClick");
    }
  };

  const cancelAndDisableForm = async () => {
    // timerRef.current.resetTimer();
    setIsEditable(false);
    await clearNextWorkflowForm();
    // unlock case workflow in case it was locked during Next Work Item click
    await lockUnlockByCaseWorkflow(0, currentCaseWorkflowIDRef.current);
    await clearCaseTabInfo();

    setCurrentCaseNumber("");
    setCurrentCaseWorkflowIDWithRef(-123456);
    setCaseInfoData(null);
    setIndexCase(false);
    setIsWorkedDocIden(false);
    isWorkedDocIdenRef.current = false;
    setSelectedDocGroupID(null);
    setSelectedDocTypeID(null);
    setDocumentSource(null);
    setNoteTemplateID(undefined);

    setShowNATReferAction(false);
    setNextActionTypeReferAction([]);
    noteForActionRef.current?.clear();
    narrativeForStateRef.current?.clear();

    setFormFieldAccess(prev => ({
      ...prev,
      nextWorkItemBtn: {
        ...prev.nextWorkItemBtn,
        hide: false
      }
    }));
  };

  const clearNextWorkflowForm = async () => {
    try {
      setCaseValidationType(EnumUtility.CaseType.Case); 
      setNoCase(false);

      clearFormStateValues();
      noteForActionRef.current?.clear();
      narrativeForStateRef.current?.clear();

      setActionOptionsState([]);
      setListOfActionsResults([]);

      setLastAddedItemId(null);
      setNextItemId(2);
      setShowNATReferAction(false);
      setNextActionTypeReferAction([]);
      setNewCaseWorkflowsList([]);

      formFieldsEnableLogic(dispatch, FormFieldEnableType.Cancel.ID);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "clearNextWorkflowForm", {});
    }
  };


  const handleCancelConfirm = async () => {
    try {
      await cancelAndDisableForm();
      // await clearCaseTabInfo();
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCancelConfirm");
    }
  };

  const handleConfirmationDialog = (dialogContent) => {
    setConfirmationDialogContent(dialogContent);
    handleOpenConfirmDialog();
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleDialogConfirmAction = async (dialogContentID) => {
    try {
      switch (dialogContentID) {

        case EnumUtility.ConfirmDialogType.NewDocumentCancel.ID:
          await handleCancelConfirm();
          break;

        case 'DuplicateWorkItem':
          if (pendingDuplicateSubmit) {
            const { workflowID, workflowName, queueName } = pendingDuplicateSubmit;
            setPendingDuplicateSubmit(null);
            setConfirmDialogOpen(false);
            await proceedWithAddWorkItem(workflowID, workflowName, queueName);
          }
          break;

        default:
          break;
      }

      setConfirmDialogOpen(false);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleDialogConfirmAction");
    }
  };

  const handleNotificationDialog = (dialogContent, timerAutoClose = false, timeToClose = 5000) => {
    setNotificationDialogContent(dialogContent);
    setTimerNotificationDialogClose(timeToClose);
    setTimerAutoCloseNotificationDialog(timerAutoClose);
    handleOpenNotificationDialog();
  };

  const handleOpenNotificationDialog = () => {
    setNotificationDialogOpen(true);
  };

  const handleCloseNotificationDialog = useCallback(() => {
    setNotificationDialogOpen(false);

    // Set focus to Case Search field if a save operation just completed
    if (shouldFocusCaseSearch) {
      setShouldFocusCaseSearch(false);
      setTimeout(() => {
        if (caseNumberRef.current) {
          caseNumberRef.current.focus();
        }
      }, 100);
    }
  }, [shouldFocusCaseSearch]);


  const handleAlertClose = () => {
    setAlertProps((prevState) => ({ ...prevState, open: false }));
  };


  const handleNotesChange = async (event) => {
    try {
      const { name, value } = event.target;
      setFormValues(prev => ({ ...prev, [name]: value }));
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNotesChange");
    }
  };

  const handleNarrativeChange = async (event, newNarrativeList) => {
    try {
      const { name, value } = event.target;
      setFormValues(prev => ({ ...prev, [name]: value }));
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNarrativeChange");
    }
  };

  const copyToClipboardFallback = (text) => {
    // Create a temporary textarea element
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // Avoid scrolling to the bottom
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0'; // Make it invisible
    document.body.appendChild(textarea);

    // Select and copy the text
    textarea.select();
    textarea.setSelectionRange(0, text.length); // Ensure all text is selected
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        console.log('Text successfully copied to clipboard');
        //alert('Text copied to clipboard!'); // Notify the user
      } else {
        throw new Error('Failed to copy text using execCommand');
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      //alert('Failed to copy text. Please try again.'); // Notify the user
    }

    // Clean up
    document.body.removeChild(textarea);
  };

  const copyToClipboard = async (textToCopy) => {
    //const textToCopy = textareaRef.current.value;

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(textToCopy)
        .then(() => {
          console.log('Text successfully copied to clipboard');
          //alert('Text copied to clipboard!'); // Optional: Notify the user
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
          // Fallback to execCommand if Clipboard API fails
          copyToClipboardFallback(textToCopy);
        });
    } else {
      // Use fallback if Clipboard API is not supported
      copyToClipboardFallback(textToCopy);
    }
  };

  const handleSaveClick = async () => {
    setIsPageLoading(true);
    try {
      await proceedWithSave();
    } catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSaveClick");
    }
  };

  // Map selectedActionOption from API response shape → C# SelectedWorkflowDetail shape
  const mapSelectedActionOption = (item) => {
    if (!item) return null;
    return {
      lastWorkflowDetailID: UtilityLibrary.toInt(item.lastWorkflowDetailID),
      displayOrder: UtilityLibrary.toInt(item.displayOrder),
      workflowDetailID: UtilityLibrary.toInt(item.workflowDetailID),
      workflowStepID: UtilityLibrary.toInt(item.workflowStepID),
      workflowDetailName: item.workflowDetailName ?? null,
      ticChoice: item.ticChoice ?? null,
      initialWorkflowStepID: UtilityLibrary.toIntOrNull(item.initialWorkflowStepID),
      initialIntervalValue: UtilityLibrary.toIntOrNull(item.initialIntervalValue),
      initialIntervalUnitID: UtilityLibrary.toIntOrNull(item.initialIntervalUnitID),
      workflowDetailValue: UtilityLibrary.toInt(item.workflowDetailValue),
      callWorkflowID: UtilityLibrary.toIntOrNull(item.callWorkflowID),
      workflowActionTypeID: UtilityLibrary.toInt(item.workflowActionTypeID),
      workflowActionTypeName: item.workflowActionTypeName ?? null,
      waT_NoteTemplateID: UtilityLibrary.toIntOrNull(item.waT_NoteTemplateID),
      terminateWorkflow: item.terminateWorkflow ?? false,
      documentTypeID: UtilityLibrary.toIntOrNull(item.documentTypeID),
      qaDispositionTypeID: UtilityLibrary.toIntOrNull(item.qaDispositionTypeID),
      workflowUnitTypeID: UtilityLibrary.toInt(item.workflowUnitTypeID),
      workflowUnitTypeName: item.workflowUnitTypeName ?? null,
      workflowCallListTypeID: UtilityLibrary.toInt(item.workflowCallListTypeID),
      workflowCallListTypeName: item.workflowCallListTypeName ?? null,
      wcL_WorkflowID: item.wcL_WorkflowID ?? null,
      wcL_WorkflowName: item.wcL_WorkflowName ?? null,
      wcL_FKM_DepartmentID: UtilityLibrary.toInt(item.wcL_FKM_DepartmentID),
    };
  };

  const onCopyFormDataToPostData = () => {
    try {
      const { loginLegacyUserID, loginSiteID, timeZone } = getUserLoginInfo();

      const notesText = noteForActionRef.current ? noteForActionRef.current.getValue() : "";
      // const narrativeText = narrativeForStateRef.current ? narrativeForStateRef.current.getValue() : "";

      const fv = formValuesRef.current;

      const selectedOption = fv.actionOptionSelectedItem;
      const isValidSelection =
        selectedOption?.workflowDetailID &&
        selectedOption.workflowDetailID !== initActionOption.workflowDetailID;

      const initialWorkflowStepID = showNATReferAction
        ? UtilityLibrary.toIntOrNull(fv.nextActionTypeReferActionSelect?.workflowStepID)
        : UtilityLibrary.toIntOrNull(fv.actionOptionSelectedItem?.initialWorkflowStepID);

      const nextStepDueDate =
        showNATReferAction && fv.nextActionDueDateTxt
          ? ymdToLocalIso(fv.nextActionDueDateTxt)
          : null;

      const daysToNext =
        (showNATReferAction && fv.nextActionDueDateTxt
          ? calculateBusinessDaysBetween(fv.nextActionDueDateTxt, holidayList)
          : fv.actionOptionSelectedItem?.initialIntervalValue) || 0;

      console.log('Selected Action Option', selectedOption);

      const addToListRow = listOfActionsResults.find(r => r.id === ADD_TO_LIST_ITEM_ID);

      const textareaNarrative = narrativeForStateRef.current?.getValue?.() ?? "";

      // const narrativeForRequest = textareaNarrative ? stripLeadingNarrativeSeparator(textareaNarrative)
      //   : stripLeadingNarrativeSeparator(addToListRow?.narrativeText || "");
      const workedDocIden = isWorkedDocIdenRef.current;
      console.log("isWorkedDocIden=====", workedDocIden);
      const postNextData = {
        "caseNumber":currentCaseNumber,
        "indexCase": indexCase,
        "isWorkedDocIden":workedDocIden,
        "caseWorkflowID": UtilityLibrary.toInt(currentCaseWorkflowIDRef.current),
        "selectedActionOption": isValidSelection ? mapSelectedActionOption(selectedOption) : null,
        "nextStepDueTS": nextStepDueDate,
        "daysToNext": daysToNext || 0,
        "userLoginID": UtilityLibrary.toInt(loginLegacyUserID),
        "masterServerDBSchema": masterServerDBSchema,
        "yTracSiteID": UtilityLibrary.toInt(loginSiteID),
        "caseWorker": caseInfoData?.caseWorker ?? null,
        "cPMemberID": null,
        "nCPMemberID": null,
        "cPFirstName": caseInfoData?.cpFirstName ?? null,
        "cPLastName": caseInfoData?.cpLastName ?? null,
        "nCPFirstName": caseInfoData?.ncpFirstName ?? null,
        "nCPLastName": caseInfoData?.ncpLastName ?? null,
        "zipCode": caseInfoData?.zipCode ?? null,
        "caseTypeID": caseInfoData?.caseTypeID != null ? UtilityLibrary.toInt(caseInfoData.caseTypeID) : null,
        "statusTypeID": caseInfoData?.statusTypeID != null ? UtilityLibrary.toInt(caseInfoData.statusTypeID) : null,
        "districtID": caseInfoData?.districtID != null ? UtilityLibrary.toInt(caseInfoData.districtID) : null,
        "countyID": caseInfoData?.countyID != null ? UtilityLibrary.toInt(caseInfoData.countyID) : null,
        "termParent": null,
        "initialWorkflowStepID": initialWorkflowStepID || null,
        "workflowMapID": null,
        "activityStartTS": actionStartTimeRef.current,
        "workflowStateLockoutTimer": 60,
        "preview": 0,
        "isNocase": noCase ? 1 : 0,
        "noteID": 0,
        "contactID": null,
        "ignoreUserOnlyLock": 0,
        "notes": notesText,
        "narrative": stripLeadingNarrativeSeparator(addToListRow?.narrativeText || ""),
        "selectedDocTypeID": selectedDocTypeID,
        "selectedDocGroupID": selectedDocGroupID,
        "waT_noteTemplateID": waT_noteTemplateID,
        "documentSource":documentSource,
        "returnedCaseWorkflowID": 0,
        "systemTimeZoneName": timeZone
      };

      return [postNextData];
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "onCopyFormDataToPostData", {});
    }
  };

  const pushNarrativeToMainframeIfNeeded = async (loginStateID, isNoCase, narrativeText) => {
    const isMFState =
      loginStateID === EnumUtility.StateIDKey.Mississippi.ID ||
      loginStateID === EnumUtility.StateIDKey.Kansas.ID;

    // Nothing to push for non-MF states, no-case, or when using mock data
    if (!isMFState || isNoCase || UtilityLibrary.isUsingMockData()) {
      return true;
    }

    try {
      const paramsForPush = {
        stateID: loginStateID,
        apiMainframeUrl: configLibrary.apiMainframeUrl,
        caseNumber: currentCaseNumber,
        narrativeText,
      };
      const responseFromPush = await ScrapeDataManager.PushNarrativeToMainframe(paramsForPush);

      // if (responseFromPush?.success === false) {
      //   showNarrativeUpdateFailure();
      //   return false;
      // }
      return true;
    } catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "pushNarrativeToMainframeIfNeeded");
      showNarrativeUpdateFailure();
      return false;
    }
  };

  const proceedWithSave = async () => {
    try {
      setIsPageLoading(true);

      const { loginLegacyUserID, loginSiteID, loginStateID, currentDateTime, timeZone } = getUserLoginInfo();
      const isMainframeIntegration = UtilityLibrary.DetermineIfMainframeHost();
      const isAPIIntegration = UtilityLibrary.DetermineIfAPIHost();
  
      console.log('CaseInfoData:', caseInfoData);

      const narrativeText = narrativeForStateRef.current ? narrativeForStateRef.current?.getValue() : "";
      const isNoCase = currentCaseNumber === 'PLACEHOLDER';
      const completedCaseWorkflowId = currentCaseWorkflowIDRef.current;
      const selectedAction = formValuesRef.current.actionOptionSelectedItem;

      const needsMFPush = isMainframeIntegration && !isNoCase && !UtilityLibrary.isUsingMockData();
      const shouldDeleteKansasAlert =
        loginStateID === EnumUtility.StateIDKey.Kansas.ID &&
        isAPIIntegration &&
        selectedAction?.terminateWorkflow === true &&
        selectedAction?.alertEnableAutomation === true &&
        !isNoCase &&
        !UtilityLibrary.isUsingMockData();

      if (needsMFPush) {
        const canContinue = await UtilityLibrary.checkMainframeConnectivityWithErrorHandling(
          configLibrary.apiMainframeUrl,
          handleNotificationDialog
        );
        if (!canContinue) {
          // Do NOT save.
          return;
        }
      }

      let postNextSaveData = { SaveExistingCaseWorkflows: [], SaveNewCaseWorkflows: [] };
      const postExistingWorkflowData = await onCopyFormDataToPostData();

      postExistingWorkflowData && postExistingWorkflowData.length > 0 && (postNextSaveData.SaveExistingCaseWorkflows = postExistingWorkflowData);
      newCaseWorkflowsList && newCaseWorkflowsList.length > 0 && (
        postNextSaveData.SaveNewCaseWorkflows = newCaseWorkflowsList.map(item => item.postComplaintsOrFollowupData)
      );

      console.log('Proceeding with save operation, postNextSaveData:', postNextSaveData.SaveExistingCaseWorkflows, postNextSaveData.SaveNewCaseWorkflows);

      const postResponseData = await ApiManager.PostExistingWorkflowSaveToApi(postNextSaveData);
      if (!postResponseData) return;

      const pushOk = await UtilityLibrary.writeExternalNarrative(loginStateID, isNoCase, narrativeText, { caseNumber: currentCaseNumber, onFailure: showNarrativeUpdateFailure, formName: errorFormName });

      if (shouldDeleteKansasAlert) {
        try {
          await ApiManager.DeleteAlertKSFromApi({
            caseWorkflowId: completedCaseWorkflowId,
            caseId: currentCaseNumber
          });
        }
        catch (error) {
          await LoggerUtility.ManageErrorLogging(error, errorFormName, "DeleteAlertKSFromApi");
          handleNotificationDialog({
            NotificationType: EnumUtility.NotificationType.Warning,
            Title: "Warning",
            Message: error?.ApiProblemDetails?.detail || "Unable to delete the KAECSES alert. Please try again, or contact the HelpDesk if the issue persists."
          });
        }
      }

      await copyToClipboard(narrativeText);

      const postSaveAPIResData = postResponseData?.[1];
      const isZeroDayAction = (postSaveAPIResData?.workflowID ?? 0) > 0 || (postSaveAPIResData?.workflowDependency?.length ?? 0) > 0;

      if (postResponseData) {
        console.log('Post response from API:', postResponseData);
        await clearNextWorkflowForm();


        if (isZeroDayAction) {
          const newCWID = postSaveAPIResData?.caseWorkflowID;

          // unlock old CaseWorkflowID and lock new CaseWorkflowID in case for refer action (0 day)
          if (newCWID > 0 && currentCaseWorkflowIDRef.current !== newCWID) {
            //Unlock ended workflow
            await lockUnlockByCaseWorkflow(0, currentCaseWorkflowIDRef.current);
            // lock new workflow
            await lockUnlockByCaseWorkflow(1, newCWID);
            // set new caseworkflow
            setCurrentCaseWorkflowIDWithRef(newCWID);
          }

          populateUIandUpdateStateWithWorkflowInfo(postSaveAPIResData);
          // search case and load right panel
          await onCaseSearch(currentCaseNumber, newCWID, CaseSearchSource.CaseNumberField.ID, isNoCase);

          handleNotificationDialog(EnumUtility.WorkflowProcess.NextWorkflowSavedSuccess, true, 3000);
          return;
        }

        await clearNextWorkflowForm();

        //no-case scenario
        if (isNoCase) {
          await cancelAndDisableForm();
          if (pushOk) {
            handleNotificationDialog(EnumUtility.WorkflowProcess.NextWorkflowSavedSuccess, true, 3000);
          }
          return;
        }

        const additionalTasks = await ApiManager.GetActiveCaseWorkflowListWithCase({
          caseNumber: currentCaseNumber,
          yTracSiteID: loginSiteID,
          masterServerDBSchema,
          userLoginID: loginLegacyUserID,
          moduleID: 1,
        }) ?? [];

        setMapAdditionalTasksData(additionalTasks);

        if (additionalTasks.length > 0) {
          handleOpenCommonDialog(DialogContentType.AdditionalTaskQueue, { Title: 'Additional Task in the Queue', minWidthCustom: 750 });
        } else {
          handleNotificationDialog(EnumUtility.WorkflowProcess.NoMoreCaseWorkflows, true, 3000);
          await cancelAndDisableForm();
        }

      }
    }
    catch (error) {//TODO call to mainframe has not been implemented yet
      const errorType = error?.Details?.type;
      if ([
        EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.Name,
        EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.Name,
        EnumUtility.CaseSearchResultCode.Enter_A_Function_Error.Name
      ].includes(errorType)) {
        let messageContentID = null;

        if (errorType === EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.Name) {
          messageContentID = ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System;
        }
        else if (errorType === EnumUtility.CaseSearchResultCode.Enter_A_Function_Error.Name) {
          messageContentID = ClientType.MessageContent.N_UI7058_ENTER_A_FUNCTION_Error_Occured;
        }
        else {
          messageContentID = ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
        }

        const messageContent = await SharedService.getMessageContent(messageContentID);
        handleNotificationDialog({ NotificationType: 'error', Message: messageContent?.messageText });
      }
      else {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "proceedWithSave");
      }
    }
    finally {
      setIsPageLoading(false); // Disable the loader
    }
  };

  const handleWorkSelectedQueuedTask = async (selectedRow) => {
    setDialogCommonState(prev => ({ ...prev, openDialog: false }));
    setMapAdditionalTasksData([]);

    if (!selectedRow) { await cancelAndDisableForm(); return; }

    const newCWID = selectedRow.caseWorkflowID;
    if (newCWID && newCWID !== currentCaseWorkflowIDRef.current) {
      // Unlock last worked caseworkflow
      await lockUnlockByCaseWorkflow(0, currentCaseWorkflowIDRef.current);
      // lock new caseworkflow
      await lockUnlockByCaseWorkflow(1, newCWID);
      setCurrentCaseWorkflowIDWithRef(newCWID);

      const isNoCase = currentCaseNumber === 'PLACEHOLDER';
      await onCaseSearch(currentCaseNumber, newCWID, CaseSearchSource.CaseNumberField.ID, isNoCase);

      setNoCase(isNoCase);
      setCaseValidationType(isNoCase ? EnumUtility.CaseType.NoCase : EnumUtility.CaseType.Case);
    }
    // Reuse the existing population logic
    populateUIandUpdateStateWithWorkflowInfo(selectedRow);
    setIsEditable(true);
    formFieldsEnableLogic(dispatch, FormFieldEnableType.NextWorkItemClick.ID);
  };

  const handleGridFilterChange = async (newFilterModel) => {
    try {
      setGridFilterModel(newFilterModel);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleGridFilterChange");
    }
  };

  const buildNarrativeText = async (actionId = ADD_TO_LIST_ITEM_ID) => {
    try {
      const { loginLegacyUserID, loginSiteID, loginStateID, currentDateTime, timeZone } = getUserLoginInfo();
      const fv = formValuesRef.current;

      let noteTemplateID = fv.actionOptionSelectedItem.waT_NoteTemplateID || 0;

      // if(!noteTemplateID) {
      //   if(fv.workflowItemSelect.workflowID !== COMPLAINT_WORKFLOW && fv.workflowItemSelect.workflowID !== FOLLOWUPS_WORKFLOW) {
      //     noteTemplateID = fv.workflowItemSelect.w_NoteTemplateID;
      //   }
      // }

      if(!noteTemplateID && actionId === ADD_TO_LIST_ITEM_ID) {
        noteTemplateID = fv.workflowSelect.w_NoteTemplateID;
      }

      if(actionId !== ADD_TO_LIST_ITEM_ID) {
        if(fv.workflowItemSelect.workflowID === COMPLAINT_WORKFLOW || fv.workflowItemSelect.workflowID === FOLLOWUPS_WORKFLOW) {
            noteTemplateID = MasterEnum.NoteTemplate.Establishment_Mainframe_Narrative_FollowUp;
        }
      }

      if(!noteTemplateID) {
        noteTemplateID =  MasterEnum.NoteTemplate.Establishment_Mainframe_Narrative;
      }

      const userLogin = JSON.parse(SessionManager.GetValueFromSession(SessionManager.UserLoginDetailsKey));
      const loginName = userLogin ? userLogin.loginName : "";
      const actionDate = UtilityLibrary.formatDate(
        UtilityLibrary.getCurrentDateTimeForTimeZone(timeZone),
        "MM/DD/YYYY"
      );

      const context = actionId === ADD_TO_LIST_ITEM_ID
        ? {
            workflowDetailName: fv.actionOptionSelectedItem.workflowDetailName || "",
            ticChoice: fv.actionOptionSelectedItem.ticChoice || "",
            loginName,
            actionDate
          }
        : {
            actionType: fv.actionTypeSelectedItem.actionType || "",
            loginName,
            actionDate
          };

      const built = await UtilityLibrary.NarrativeBuilder({ YTracSiteID: loginSiteID, NoteTemplateID: noteTemplateID }, context);
      return String(built || "").trim();
      // (built ?? "").replace(/^\s*\|\s*/, ""); 
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "buildNarrativeText");
    }
  };

  const onLoadNarrativeForWorkflow = async () => {
    try {
      let narrativeText = "";

      // Skip narrative generation for No Case workflows
      if (formValues.noCaseChk) {

        if (narrativeForStateRef.current) {
          narrativeForStateRef.current.setValue("");
        }

        const syntheticEvent = {
          target: {
            name: 'narrativeForStateTxt',
            value: ""
          }
        };
        await handleNarrativeChange(syntheticEvent);
        return;
      }

      const { loginLegacyUserID, loginSiteID, loginStateID, currentDateTime, timeZone } = getUserLoginInfo();
      const userLogin = JSON.parse(SessionManager.GetValueFromSession(SessionManager.UserLoginDetailsKey));

      // Manually trigger the handleNarrativeChange to update form state and validation
      const syntheticEvent = {
        target: {
          name: 'narrativeForStateTxt',
          value: narrativeText
        }
      };
      await handleNarrativeChange(syntheticEvent);

      console.log('Narrative: ' + narrativeText);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "onLoadNarrativeForWorkflow", {});
    }
  };

  const calculateBusinessDaysBetween = (endDateStr, holidays) => {
    if (!endDateStr) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = endDateStr.split('-').map(Number);
    const end = new Date(y, m - 1, d);
    end.setHours(0, 0, 0, 0);
    if (end <= today) return 0;
    const holidayTimestamps = new Set(
      (holidays || []).map(h => {
        const hd = new Date(h);
        hd.setHours(0, 0, 0, 0);
        return hd.getTime();
      })
    );
    let count = 0;
    const current = new Date(today);
    current.setDate(current.getDate() + 1);
    while (current <= end) {
      const day = current.getDay();
      if (day !== 0 && day !== 6 && !holidayTimestamps.has(current.getTime())) {
        count++;
      }
      current.setDate(current.getDate() + 1);
    }
    return count;
  };

  const handleAddToListClick = async () => {
    dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtnEnabled", isEnabled: false } });
    dispatch({ type: "SET_FIELD_STATE", payload: { field: "addNewItemBtnEnabled", isEnabled: true } });

    // hadle logic to add the selected workflow step to the action options list
    await new Promise(resolve => setTimeout(resolve, 10)); // Ensure formValues are updated before proceeding

    // Read latest form values AFTER the awaits above
    const fv = formValuesRef.current;

    const caseWorkflowID = currentCaseWorkflowIDRef.current;
    const { loginLegacyUserID, loginSiteID, loginStateID, currentDateTime, timeZone } = getUserLoginInfo();

    try {

      if (!caseWorkflowID || caseWorkflowID <= 0) {
        console.warn("Invalid caseWorkflowID:", caseWorkflowID);
        return;
      }

      if (!fv.actionOptionSelectedItem?.workflowDetailID) {
        console.warn("No workflow detail selected");
        return;
      }

      if (!loginLegacyUserID) {
        console.warn("User login ID not available");
        return;
      }

      const initWorkflowStepID = showNATReferAction
        ? UtilityLibrary.toIntOrNull(fv.nextActionTypeReferActionSelect?.workflowStepID)
        : UtilityLibrary.toIntOrNull(fv.actionOptionSelectedItem?.initialWorkflowStepID);

      const nextStepDueDate = showNATReferAction && fv.nextActionDueDateTxt ? ymdToLocalIso(fv.nextActionDueDateTxt) : null;

      const params = {
        caseWorkflowID: UtilityLibrary.toInt(caseWorkflowID),
        workflowDetailID: UtilityLibrary.toInt(fv.actionOptionSelectedItem.workflowDetailID),
        nextStepDueTS: nextStepDueDate,
        userLoginID: UtilityLibrary.toInt(loginLegacyUserID),
        masterServerDBSchema: masterServerDBSchema,
        yTracSiteID: UtilityLibrary.toInt(loginSiteID),
        initialWorkflowStepID: initWorkflowStepID === -123456 ? null : initWorkflowStepID,
        workflowMapID: null,
        activityStartTS: actionStartTimeRef.current,
        preview: 1
      };

      console.log("Calling PreviewTakeActionAddToList with params:", params); // Debug logging

      const addToList = await ApiManager.PreviewTakeActionAddToList(params);

      if (addToList && addToList.length > 0) {
        const item = addToList[0];

        let newItem = {};

        if (item.workflowID === 0 || item.queueID === 0) {
          newItem = {
            id: ADD_TO_LIST_ITEM_ID,
            action: fv.actionOptionSelectedItem.workflowDetailName || '',
            workflowID: item.workflowID || 0,
            workflow: '',
            queueID: item.queueID || 0,
            queue: '',
            daysToNext: '',
            workflowStepID: item.workflowStepID || 0,
            nextActionType: ''
          };
        } else {
          const calculatedDaysToNext = showNATReferAction ? calculateBusinessDaysBetween(fv.nextActionDueDateTxt, holidayList) : 0;
          newItem = {
            id: ADD_TO_LIST_ITEM_ID,
            action: fv.actionOptionSelectedItem.workflowDetailName || '',
            workflowID: item.workflowID || 0,
            workflow: item.workflowName || '',
            queueID: item.queueID || 0,
            queue: item.queueName || '',
            daysToNext: calculatedDaysToNext === 0 ? (fv.actionOptionSelectedItem?.initialIntervalValue || 0) : calculatedDaysToNext,
            workflowStepID: item.workflowStepID || 0,
            nextActionType: item.workflowStepName || ''
          };
        }

        const narrativeText = await buildNarrativeText(ADD_TO_LIST_ITEM_ID);
        newItem.narrativeText = narrativeText;

        // Add single item to existing results
        setListOfActionsResults(prevResults => [newItem, ...prevResults.filter(r => r.id !== ADD_TO_LIST_ITEM_ID)]);

        setLastAddedItemId(newItem.id);

        console.log("Successfully added to list:", newItem);
      } else {
        console.log("API call successful but no results returned");
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtnEnabled", isEnabled: true } });
        dispatch({ type: "SET_FIELD_STATE", payload: { field: "addNewItemBtnEnabled", isEnabled: false } });
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleAddToListClick");
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "addToListBtnEnabled", isEnabled: true } });
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "addNewItemBtnEnabled", isEnabled: false } });
    }
  };

  // Disable "Add New Item" button when data grid is empty
  useEffect(() => {
    if (listOfActionsResults.length === 0) {
      dispatch({ type: "SET_FIELD_STATE", payload: { field: "addNewItemBtnEnabled", isEnabled: false } });
      setLastAddedItemId(null);
    }

    const parts = listOfActionsResults.map(item => item.narrativeText).filter(t => t && t.trim() !== '');

    const raw = UtilityLibrary.Multipart_Narrative_Consolidator(parts);
    const consolidated = raw.replace(/^\s*\|\s*/, ""); // Remove leading pipe and whitespace
    narrativeForStateRef.current?.setValue(consolidated);

    setFormValues(prev => prev.narrativeForStateTxt === consolidated ? prev : { ...prev, narrativeForStateTxt: consolidated });  
  }, [listOfActionsResults]);

  const buildComplainAndFollowupRequest = (newId, narrativeText="", workflowID) => {
    // Read latest form values (called synchronously from proceedWithAddWorkItem)
    const fv = formValuesRef.current;

    const { loginLegacyUserID, loginSiteID, loginStateID, timeZone } = getUserLoginInfo();

    const workflowMapID = UtilityLibrary.toIntOrNull(addWorkItemNAT.workflowMapID) || null;
    const startWorkflowStepID = UtilityLibrary.toInt(addWorkItemNAT.workflowStepID) || 0;
    

    const postComplaintsOrFollowupData = {
      "workflowID": workflowID,
      "caseNumber": currentCaseNumber,
      "isNocase": noCase ? 1 : 0,
      "notes": fv.noteForActionTxt,
      "narrative": stripLeadingNarrativeSeparator(narrativeText),
      "yTracSiteID": loginSiteID,
      "masterServerDBSchema": masterServerDBSchema,
      "actionStartTime": newWorkItemStartTimeRef.current,
      "userLoginID": loginLegacyUserID,
      "startWorkflowStepID": startWorkflowStepID,
      "workflowMapID": workflowMapID,
      "yTracSite_StateID": loginStateID,
      "nextStepDueTS": null,
      "moduleID": 1, // To-do: Modify Code - Change Hard-Coded value of ModuleID
      "systemTimeZoneName": timeZone,
    };

    return [{ newId, postComplaintsOrFollowupData }];
  };

  const handleAddWorkItemSubmit = async () => {
    try {
      if (!dialogFormState.addWorkItemButtonEnabled) {
        console.warn("Add Work Item button should be disabled");
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 10));

      // Read latest form values AFTER the awaits above
      const fv = formValuesRef.current;

      const workflowID  = fv.workflowItemSelect?.workflowID || 0;
      const workflowName = fv.workflowItemSelect?.workflowName || "";
      const queueName = fv.workflowItemSelect?.queueName || "";

      const isDuplicate =
        listOfActionsResults.some(item => item.workflowID === COMPLAINT_WORKFLOW && item.workflowStartTypeID === fv.actionTypeSelectedItem.workflowStartTypeID)
        || listOfActionsResults.some(item => item.workflowID === FOLLOWUPS_WORKFLOW && item.workflowStartID === fv.iccSelect.workflowStartID);

      if (isDuplicate) {
        setPendingDuplicateSubmit({ workflowID, workflowName, queueName });
        handleConfirmationDialog({
          ID: 'DuplicateWorkItem',
          NotificationType: 'warning',
          Title: 'Warning',
          Message: `There is already a queued action for: ${fv.actionTypeSelectedItem.actionType}. Do you want to queue this action again?`
        });
        return;
      }

      await proceedWithAddWorkItem(workflowID, workflowName, queueName);
      handleCloseCommonDialog();
    } catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleAddWorkItemSubmit");
    }
  };

  const proceedWithAddWorkItem = async (workflowID, workflowName, queueName) => {
    // Read latest form values (this also runs from the duplicate-confirm dialog, a different closure)
    const fv = formValuesRef.current;

    const newId = nextItemId;
    setNextItemId(prev => prev + 1);

    const narrativeText = await buildNarrativeText(newId);

    let actionType = fv.actionTypeSelectedItem.actionType || '';

    if (fv.iccSelect.dropdownName) {
      actionType = fv.actionTypeSelectedItem.actionType + "-" + fv.iccSelect.dropdownName;
    }

    const newItem = {
      id: newId,
      action: actionType,
      workflowID: workflowID || 0,
      workflow: workflowName,
      queue: queueName,
      daysToNext: siteSettingDetail.DaysToNextFollowup || 0,
      nextActionType: addWorkItemNAT.workflowStepName || '',
      workflowStartTypeID: fv.actionTypeSelectedItem.workflowStartTypeID || 0,
      workflowStartID: fv.iccSelect.workflowStartID || 0,
      narrativeText,
    };

    console.log("Complaints or Followup:", [newItem]);
    const currentTextareaValue = narrativeForStateRef.current?.getValue?.() ?? fv.narrativeForStateTxt ?? "";

    // setListOfActionsResults(prevResults => [...prevResults, newItem]);
    setListOfActionsResults(prevResults => {
      let patched = prevResults;

      if (currentTextareaValue && prevResults.length > 0) {
        const parts = currentTextareaValue.split(/\s{2}\|\s/);

        // Only remap when the split matches the current row count; otherwise the
        // user has changed structure and we can't safely realign — leave as-is.
        if (parts.length === prevResults.length) {
          patched = prevResults.map((row, idx) => {
            const p = (parts[idx] ?? "").trim();
            const nextNarrative = p ? `| ${p}` : row.narrativeText;
            return nextNarrative === row.narrativeText
              ? row
              : { ...row, narrativeText: nextNarrative };
          });
        }
      }

      return [...patched, newItem];
    });
    const newWorkflow = buildComplainAndFollowupRequest(newId, narrativeText, workflowID);

    setNewCaseWorkflowsList(prev => [...prev, ...newWorkflow]);
    console.log("New CaseWorkflow:", newCaseWorkflowsList);

    setActionItems([]);
    setIccItems([]);
    setIccEnabled(false);

    UtilityLibrary.updateArrayStateValues(setFormValues, {
      workflowItemSelect: initWorkflow,
      actionTypeSelectedItem: initActionTypeSelectedItem,
      iccSelect: initICCSelect
    });

    dialogDispatch({ type: DialogFormFieldEnableType.WorkflowSelect.ID });
  };


  const handleNewWorkItemClick = async () => {
    const contentType = DialogContentType.AddNewWorkItem;
    const contentProps = {
      Title: 'Add New Work Item',
      // minHeightCustom: 400,
    };

    const { loginLegacyUserID, loginSiteID, timeZone } = getUserLoginInfo();
    newWorkItemStartTimeRef.current = UtilityLibrary.getCurrentDateTimeForTimeZone(timeZone);

    const params = {
      YTracSiteID: loginSiteID,
      masterServerDBSchema: masterServerDBSchema,
      FKM_UserLoginID: loginLegacyUserID,
    };

    const workflowTypesApiData = await ApiManager.GetNextTabEnableWorkflowsList(params);
    console.log(`workflowTypesApiData: ${workflowTypesApiData}`);
    setWorkflowItems(workflowTypesApiData || []);

    handleOpenCommonDialog(contentType, contentProps);
  };

  const handleWorkflowSelectChange = async (event, newValue, reason) => {
    try {
      let selectedWorkflowValue = { ...initWorkflow };
      let isIccViewEnabled = false;

      if (reason !== "clear" && newValue) {
        selectedWorkflowValue = newValue;
        isIccViewEnabled = newValue.enableAddTabInputChannelChoice;
        setIccEnabled(isIccViewEnabled);
        const param = {
          workflowID: newValue.workflowID,
        };
        const actionTypeList = await fetchActionTypeList(param);
        setActionItems(actionTypeList || []);
        // Dispatch to dialog reducer
        dialogDispatch({
          type: DialogFormFieldEnableType.WorkflowSelect.ID
        });
      } else {
        setIccEnabled(false);
        setActionItems([]);
        dialogDispatch({ type: DialogFormFieldEnableType.WorkflowSelectClear.ID });
      }

      setFormValues(prev => ({ ...prev, workflowItemSelect: selectedWorkflowValue }));
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleWorkflowSelectChange");
    }
  };

  const fetchActionTypeList = async (param) => {
    try {
      const actionTypeList = await ApiManager.GetActionTypeList(param);
      const rowsWithIds = actionTypeList.map((item, index) => ({
        ...item,
        actionTypeID: (index + 1)
      }));
      return rowsWithIds || [];
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchActionTypeList");
    }
  };

  const handleActionTypeSelectChange = async (event, newValue, reason) => {
    try {
      let selectedActionTypeValue = { ...initActionTypeSelectedItem };

      if (reason !== "clear" && newValue) {
        selectedActionTypeValue = newValue;

        if (iccEnabled) {
          const param = {
            workflowID: newValue.workflowID,
            workflowStartTypeID: newValue.workflowStartTypeID
          };

          const iccList = await fetchICCList(param);
          setIccItems(iccList || []);

          // Dispatch to reducer - ICC required
          dialogDispatch({
            type: DialogFormFieldEnableType.ActionTypeSelect.ID,
            payload: { enableICC: true }
          });
        } else {
          const param = {
            workflowID: selectedActionTypeValue.workflowID,
            workflowStartID: selectedActionTypeValue.workflowStartID,
          };

          const nextActionType = await fetchAddWorkItemNAT(param);
          setAddWorkItemNAT(nextActionType[0] || { ...initSelectedNAT });

          // Dispatch to reducer - no ICC required
          dialogDispatch({ type: DialogFormFieldEnableType.ActionTypeSelect.ID, payload: { enableICC: false } });

          setFormValues(prev => ({ ...prev, actionTypeSelectedItem: selectedActionTypeValue }));
        }
      } else {
        // Dispatch to reducer - Action Type cleared
        dialogDispatch({
          type: DialogFormFieldEnableType.Clear.ID
        });
      }
      setFormValues(prev => ({ ...prev, actionTypeSelectedItem: selectedActionTypeValue }));
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleActionTypeSelectChange");
    }
  };

  const fetchICCList = async (param) => {
    try {
      const params = {
        workflowID: param.workflowID,
        workflowStartTypeID: param.workflowStartTypeID,
      };
      const iccListApiData = await ApiManager.GetIccTypeList(params);

      const rowsWithIds = iccListApiData.map((item, index) => ({
        ...item,
        iccTypeID: (index + 1)
      }));
      return rowsWithIds || [];
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchICCList");
    }
  };

  const fetchAddWorkItemNAT = async (paramsObj) => {
    const loginUserInfo = sessionService.getUser();
    const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;

    try {
      const params = {
        workflowID: paramsObj.workflowID,
        workflowStartID: paramsObj.workflowStartID,
        masterServerDBSchema,
        YTracSiteID: loginStateID,
      }
      const nextActionTypesApiData = await ApiManager.GetNextActionTypeList(params);
      const rowsWithIds = nextActionTypesApiData.map((item, index) => ({
        ...item,
        nextActionTypeID: (index + 1)
      }));

      return rowsWithIds;
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchAddWorkItemNAT");
    }
  };

  const handleICCSelectChange = async (event, newValue, reason) => {
    try {
      let selectedICCValue = { ...initICCSelect };
      let nextActionType = [{ ...initSelectedNAT }];

      if (reason !== "clear" && newValue) {
        selectedICCValue = newValue;

        if (selectedICCValue.iccTypeID !== -123456) {

          const param = {
            workflowID: selectedICCValue.workflowID,
            workflowStartID: selectedICCValue.workflowStartID,
          };

          nextActionType = await fetchAddWorkItemNAT(param);
          setAddWorkItemNAT(nextActionType[0] || { ...initSelectedNAT });
        }

        // Dispatch to reducer - ICC selected, enable button
        dialogDispatch({ type: DialogFormFieldEnableType.ICCSelect.ID });
      } else {
        // Clear action - reset to previous state
        dialogDispatch({
          type: DialogFormFieldEnableType.ActionTypeSelect.ID,
          payload: { enableICC: true }
        });
      }
      setFormValues(prev => ({
        ...prev,
        iccSelect: selectedICCValue,
        addWorkItemNATSelect: nextActionType[0] || { ...initSelectedNAT }
      }));
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleICCSelectChange");
    }
  };


  const handleAddTaskQueueClick = () => {
    const contentType = DialogContentType.AdditionalTaskQueue;
    const contentProps = {
      Title: 'Additional Task in the Queue',
      minWidthCustom: 750,
    };
    handleOpenCommonDialog(contentType, contentProps);
  };

  const handleOpenCommonDialog = (contentType, contentProps = {}) => {
    setDialogCommonState({
      openDialog: true,
      contentType: contentType, // 'search', 'form', 'preview', etc.
      contentProps: contentProps
    });
  };

  const handleCloseCommonDialog = () => {
    const closingContentType = dialogCommonState.contentType;

    setDialogCommonState(prev => ({ ...prev, openDialog: false }));

    if (closingContentType === DialogContentType.AdditionalTaskQueue) {
      setMapAdditionalTasksData([]);
      cancelAndDisableForm();
      return;
    }

    // Clear workflow selection and reset form values for dialog
    setFormValues(prev => ({
      ...prev,
      workflowItemSelect: initWorkflow,
      actionTypeSelectedItem: initActionTypeSelectedItem,
      iccSelect: initICCSelect
    }));

    // Reset dialog form state to initial
    dialogDispatch({ type: DialogFormFieldEnableType.Clear.ID });

    // Clear dropdown arrays
    setActionItems([]);
    setIccItems([]);
    setIccEnabled(false);
    setAddWorkItemNAT(initSelectedNAT);
    setMapAdditionalTasksData([]);
  };

  const fetchNATReferAction = async (workflowDetailID) => {
    const { loginLegacyUserID, loginSiteID, loginStateID, currentDateTime, timeZone } = getUserLoginInfo();

    try {
      const params = {
        UserLoginID: loginLegacyUserID,
        WorkflowDetailID: workflowDetailID,
        masterServerDBSchema,
        YTracSiteID: loginStateID,
      }

      const nextActionTypesApiData = await ApiManager.GetNextActionTypeListReferAction(params);

      setNextActionTypeReferAction(nextActionTypesApiData || []);

      if (nextActionTypesApiData.length > 1) {
        setFormValues(prev => ({
          ...prev,
          nextActionTypeReferActionSelect: { ...initWorkflowStep }
        }));
      } else if (nextActionTypesApiData.length === 1) {
        setFormValues(prev => ({
          ...prev,
          nextActionTypeReferActionSelect: nextActionTypesApiData[0]
        }));
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchNATReferAction");
    }
  };

  const handleNATReferActionChange = async (e, newValue, reason) => {
    try {
      const selectedNAT = reason === "clear" || !newValue ? { ...initWorkflowStep } : { ...newValue };

      const isValidNAT = selectedNAT.workflowStepID && selectedNAT.workflowStepID !== initWorkflowStep.workflowStepID;
      
      setFormValues(prev => ({ ...prev, nextActionTypeReferActionSelect: selectedNAT }));
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNATReferActionChange");
    }
  };

  const isAddToListNATBlocking = showNATReferAction && (() => {
    const isSingleNAT = nextActionTypeReferAction.length === 1;
    const hasValidNATSelected =
      formValues.nextActionTypeReferActionSelect?.workflowStepID &&
      formValues.nextActionTypeReferActionSelect.workflowStepID !== initWorkflowStep.workflowStepID;
    // Mirror the same isDateDisabled logic used in the DatePicker
    const isDateDisabled = isSingleNAT || !hasValidNATSelected || !isUserAllowedNextActionDue();
    // Block if: no NAT selected, OR date is enabled but not yet picked
    return !hasValidNATSelected || (!isDateDisabled && !formValues.nextActionDueDateTxt);
  })();

  const DialogContentManager = {
    [DialogContentType.AddNewWorkItem]: (
      <AddNewWorkItemDialog
        workflowItems={workflowItems}
        actionItems={actionItems}
        iccItems={iccItems}
        formValues={formValues}
        dialogFormState={dialogFormState}
        iccEnabled={iccEnabled}
        onWorkflowChange={handleWorkflowSelectChange}
        onActionTypeChange={handleActionTypeSelectChange}
        onICCChange={handleICCSelectChange}
        onSubmit={handleAddWorkItemSubmit}
      />
    ),
    [DialogContentType.AdditionalTaskQueue]: (
      <AdditionalTaskQueueDialog
        caseNumber={currentCaseNumber}
        rows={mapAdditionalTasksData}
        onClose={handleCloseCommonDialog}
        onWork={handleWorkSelectedQueuedTask}
      />
    ),
  };

  const leftPaneContent = (
    <div className="content-main-container">
      {!isEditable && (
        <div className="banner-blue">
          <div className="button-group">
            <div className="base-wrapper">
              {!(formFieldAccess.nextWorkItemBtn?.hide ?? false) && (
                <div className="base-2">
                  <img className="masked-icon" alt="Masked icon" src='/doc-New.png' />
                  <Button id="nextWorkItemBtn" name="nextWorkItemBtn" className="button-2" onClick={handleNextWorkItemClick} disabled={!(formFieldAccess.nextWorkItemBtn?.write ?? true)}>NEXT WORK ITEM</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="pane-wrap-container">
        <div className={`${isEditable ? 'pane-main-container' : 'pane-main-container-2 greyed-out'}`}>
          <div className={`${isPageLoading ? ' greyed-out' : isEditable ? 'form-section editable' : 'form-section-2 '}`}>
            <div className="text-wrapper-header-bold">Work Item Details</div>
            <div className={`${isEditable ? 'form-main-container' : 'form-main-container-2'}`}>
              {/* Header Section */}
              <header className="form-header">

              </header>
              {/* Form Section */}
              <div className='form-content'>
                <div className='form-dynamic-content'>
                  <form className="form-dynamic-grid">

                    {/* Field 1 */}
                    <div className='form-row'>
                      <div className='form-group'>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FormControl fullWidth>
                            <TextField
                              id="workflowSelect"
                              name="workflowSelect"
                              label="Workflow"
                              variant="outlined"
                              value={formValues?.workflowSelect?.workflowName || ""}
                              InputProps={{
                                readOnly: true,
                              }}
                              slotProps={{
                                inputLabel: {
                                  shrink: true,
                                },
                              }}
                              fullWidth
                              sx={{
                                "& .MuiInputBase-input": {
                                  pointerEvents: "none",
                                  userSelect: "none",
                                  caretColor: "transparent",
                                  cursor: "default",
                                },
                                "& .MuiOutlinedInput-root": {
                                  cursor: "default",
                                },
                              }}
                            />
                          </FormControl>
                          <label>
                            <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.workflowSelect}</span>
                          </label>
                        </div>

                      </div>
                      <div className='form-group'>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FormControl fullWidth>
                            <TextField
                              id="lastActionSelect"
                              name="lastActionSelect"
                              label="Last Action"
                              variant="outlined"
                              value={formValues?.lastActionSelect || ""}
                              InputProps={{
                                readOnly: true,
                              }}
                              slotProps={{
                                inputLabel: {
                                  shrink: true,
                                },
                              }}
                              fullWidth
                              sx={{
                                "& .MuiInputBase-input": {
                                  pointerEvents: "none",
                                  userSelect: "none",
                                  caretColor: "transparent",
                                  cursor: "default",
                                },
                                "& .MuiOutlinedInput-root": {
                                  cursor: "default",
                                },
                              }}
                            />
                          </FormControl>
                          <label>
                            <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.lastActionSelect}</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* NAT 1 */}
                    <div className='form-row'>
                      <div className='form-group'>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FormControl fullWidth>
                            <TextField
                              id="nextActionTypeSelect"
                              name="nextActionTypeSelect"
                              label="Next Action Type"
                              variant="outlined"
                              value={formValues?.nextActionTypeSelect?.workflowStepName || ""}
                              InputProps={{
                                readOnly: true,
                              }}
                              slotProps={{
                                inputLabel: {
                                  shrink: true,
                                },
                              }}
                              fullWidth
                              sx={{
                                "& .MuiInputBase-input": {
                                  pointerEvents: "none",
                                  userSelect: "none",
                                  caretColor: "transparent",
                                  cursor: "default",
                                },
                                "& .MuiOutlinedInput-root": {
                                  cursor: "default",
                                },
                              }}
                            />
                          </FormControl>
                          <label>
                            <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.nextActionTypeSelect}</span>
                          </label>
                        </div>
                      </div>
                      <div className='form-group'>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FormControl fullWidth>
                            <TextField
                              id="nextActionDueDate"
                              name="nextActionDueDate"
                              label="Next Action Due"
                              variant="outlined"
                              value={formValues?.nextActionDueDateformValue || ""}
                              InputProps={{
                                readOnly: true,
                                tabIndex: -1,
                              }}
                              slotProps={{
                                inputLabel: {
                                  shrink: true,
                                },
                              }}
                              fullWidth
                              sx={{
                                "& .MuiInputBase-input": {
                                  pointerEvents: "none",
                                  userSelect: "none",
                                  caretColor: "transparent",
                                  cursor: "default",
                                },
                                "& .MuiOutlinedInput-root": {
                                  cursor: "default",
                                },
                              }}
                            />
                          </FormControl>
                          <label>
                            <span style={{ width: "10px", display: "inline-block", textAlign: "center", color: "red", }}>
                              {requiredFieldLabels.nextActionDueDateformValue}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* action taken */}
                    <div className='form-row'>
                      <div className='form-group'>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FormControl fullWidth>
                            <Autocomplete
                              options={actionOptionsState}
                              getOptionLabel={(option) => option.workflowDetailName || ""}
                              isOptionEqualToValue={(option, value) => option.workflowDetailID === value?.workflowDetailID}
                              id="actionTakenRequiredSelect"
                              value={actionOptionsState.find((opt) => opt.workflowDetailID === formValues.actionOptionSelectedItem.workflowDetailID) || null}
                              iconcomponent={ArrowDropDown}
                              label="Action Taken"
                              noOptionsText="Action Taken not found"
                              disabled={lastAddedItemId !== null}
                              onChange={(event, newValue) => {
                                const modifiedEvent = {
                                  target: {
                                    name: "actionOptionSelect",
                                  },
                                };
                                handleActionOptionChange(modifiedEvent, newValue);
                              }}
                              renderInput={(params) => (
                                <TextField {...params}
                                  label="Action Taken"
                                  variant="outlined"
                                  InputLabelProps={{
                                    shrink: true, // Always keeps the label shrunk
                                  }}
                                />)}
                            >
                            </Autocomplete>
                          </FormControl>
                          <label>
                            <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.actionTakenRequiredSelect}</span>
                          </label>
                        </div>
                      </div>

                    </div>

                    {/* NAT 2 */}
                    {showNATReferAction && (
                      <div className='form-row'>
                        <div className='form-group'>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FormControl fullWidth>
                              <Autocomplete
                                options={nextActionTypeReferAction}
                                getOptionLabel={(option) => option.workflowStepName || ""}
                                isOptionEqualToValue={(option, value) => option.workflowStepID === value?.workflowStepID}
                                id="nextActionTypeReferActionSelect"
                                value={nextActionTypeReferAction.find((opt) => opt.workflowStepID === formValues.nextActionTypeReferActionSelect?.workflowStepID) || null}
                                iconcomponent={ArrowDropDown}
                                label="Next Action Type"
                                noOptionsText="Next Action Type not found"
                                disabled={nextActionTypeReferAction.length === 1 || lastAddedItemId !== null}
                                onChange={(event, newValue) => {
                                  const modifiedEvent = {
                                    target: {
                                      name: "nextActionTypeReferActionSelect",
                                    },
                                  };
                                  handleNATReferActionChange(modifiedEvent, newValue);
                                }}
                                renderInput={(params) => (
                                  <TextField {...params}
                                    label="Refer Next Action Type"
                                    variant="outlined"
                                    InputLabelProps={{
                                      shrink: true, // Always keeps the label shrunk
                                    }}
                                  />)}
                              >
                              </Autocomplete>
                            </FormControl>
                            <label>
                              <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>{requiredFieldLabels.nextActionTypeReferAction}</span>
                            </label>
                          </div>
                        </div>
                        <div className='form-group'>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {(() => {
                              const isDateDisabled = nextActionTypeReferAction.length === 1 || lastAddedItemId !== null || !isUserAllowedNextActionDue();
                              const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
                              return (
                                <FormControl fullWidth>
                                  <DatePicker
                                    selected={
                                      formValues.nextActionDueDateTxt
                                        ? (() => {
                                          const [y, m, d] = formValues.nextActionDueDateTxt.split('-').map(Number);
                                          return new Date(y, m - 1, d);
                                        })()
                                        : null
                                    }
                                    onChange={handleNextActionDueDateChange}
                                    excludeDates={holidayList}
                                    minDate={today}
                                    maxDate={getNYDateToday()}
                                    disabled={isDateDisabled}
                                    filterDate={(date) => {
                                      const day = date.getDay();
                                      return day !== 0 && day !== 6;
                                    }}
                                    placeholderText="Select date"
                                    customInput={
                                      <TextField
                                        label="Refer Next Action Due"
                                        variant="outlined"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                          '& .MuiInputBase-input': { color: isDateDisabled ? '#666' : '#000' },
                                          '& .MuiInputBase-input::placeholder': { color: '#666', opacity: 1 },
                                          '& .MuiOutlinedInput-root': {
                                            '&.Mui-focused': {
                                              '& .MuiIconButton-root .MuiSvgIcon-root': { color: '#5A73EA !important' }
                                            }
                                          }
                                        }}
                                        InputProps={{
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              <IconButton disabled={isDateDisabled} onClick={handleNextActionDueDateChangeClear}>
                                                <ClearIcon sx={{ color: '#666' }} />
                                              </IconButton>
                                            </InputAdornment>
                                          )
                                        }}
                                      />
                                    }
                                  />
                                </FormControl>
                              );
                            })()}
                          </div>
                        </div>
                      </div>)
                    }

                    {/* add to List */}
                    <div className='form-row'>
                      <div className='form-group'>
                        <div className="d-flex justify-content-end align-items-center mb-2 form-actions-2">
                          <div >
                            {!(formFieldAccess.addToListBtn?.hide ?? false) && (
                              <Button
                                id="addToListBtn"
                                name="addToListBtn"
                                variant='contained'
                                color='ywButtonBlue'
                                className='me-3'
                                disabled={!formState.addToListBtnEnabled}
                                onClick={handleAddToListClick}
                              >
                                ADD TO LIST
                              </Button>
                            )}
                          </div>
                          <div >
                            {!(formFieldAccess.addNewItemBtn?.hide ?? false) && (
                              <Button
                                id="addNewItemBtn"
                                name="addNewItemBtn"
                                variant="text"
                                color="ywTextBlue"
                                className='me-3'
                                onClick={handleNewWorkItemClick}
                                disabled={!formState.addNewItemBtnEnabled}
                              >
                                <AddIcon />ADD NEW ITEM
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='form-row'>
                      <div className='form-group'>
                        {!(formFieldAccess.noteForActionTxt?.hide ?? false) && (
                          <div className="col-12 mb-3 align-items-center justify-center">
                            <TextAreaWithCount
                              ref={noteForActionRef}
                              id="noteForActionTxt"
                              nameForField="noteForActionTxt"
                              labelValue="Y-Trac Note for Action Taken"
                              maxLength={notesCharLimit}
                              placeholder=""
                              onTextChange={handleNotesChange}
                              isDisabled={!formState.noteForActionTxt}
                              requiredFieldLabel={requiredFieldLabels.noteForActionTxt}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='form-row'>
                      <div className='form-group'>
                        {!(formFieldAccess.narrativeForStateTxt?.hide ?? false) && (
                          <div className="col-12 mb-3 align-items-center justify-center">
                            <TextAreaWithCount
                              ref={narrativeForStateRef}
                              id="narrativeForStateTxt"
                              nameForField="narrativeForStateTxt"
                              labelValue="Narrative for State System"
                              maxLength={narrativeCharLimit}
                              placeholder=""
                              onTextChange={handleNarrativeChange}
                              isDisabled={!formState.narrativeForStateTxt}
                              requiredFieldLabel={requiredFieldLabels.narrativeForStateTxt}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                  </form>

                  {/* DataGrid */}
                  {!(formFieldAccess.listOfActionsDgrd?.hide ?? false) && (
                    <div className="data-grid-container">
                      {/* DataGrid for List of Actions */}
                      <div className="yt-button-container">
                        <div className="text-wrapper-header-bold-2" style={{ marginTop: '5px' }}>List of Actions</div>


                      </div>
                      <div style={{ marginTop: '2px', height: '100%', width: "100%" }}>
                        {hasListOfActions ?
                          <DataGridPremium
                            id="listOfActionsDgrd"
                            name="listOfActionsDgrd"
                            rows={listOfActionsResults}
                            columns={listOfActionsGridColumns}
                            rowsPerPageOptions={[5, 10, 15, 20, 25]}
                            pagination
                            pageSizeOptions={[5, 10, 15, 20, 25]}
                            paginationModel={paginationModel} // Controlled pagination
                            onPaginationModelChange={setPaginationModel} // Handle page changes                    
                            getRowId={(row) => row.id}
                            filterModel={gridFilterModel}
                            onFilterModelChange={handleGridFilterChange}
                            disableSelectionOnClick
                            sx={{
                              "& .MuiDataGrid-row:hover": {
                                cursor: "pointer"
                              },
                              ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
                                "margin-top": "1em",
                                "margin-bottom": "1em"
                              },
                              "& .MuiDataGrid-virtualScroller": { overflowX: "auto", overflowY: "auto" }
                            }}
                          />
                          : initialSearch && (<div className="text-wrapper-subtitle">No action(s) found!</div>)
                        }
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* Footer Section */}
              <footer className="form-footer">
                <div className="d-flex justify-content-between align-items-center mb-2 form-actions">
                  {!(formFieldAccess.cancelBtn?.hide ?? false) && (
                    <div>
                      <Button id="cancelBtn" name="cancelBtn" color="ywButtonWhite" variant='outlined' onClick={handleCancelClick}>Cancel</Button>
                    </div>
                  )}
                  {!(formFieldAccess.leftPaneMessageLbl?.hide ?? false) && (
                    <div className="center-section text-wrapper-message">
                      <div><label id="leftPaneMessageLbl" name="leftPaneMessageLbl">{leftPaneMessage}</label></div>
                      {isTimerVisible &&
                        <div><label className='timer-message'>Timer: {timerView}</label></div>
                      }
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "18px" }}>
                    {!(formFieldAccess.saveBtn?.hide ?? false) && (
                      <Button
                        id="saveBtn"
                        name="saveBtn"
                        color="ywButtonBlack"
                        variant='contained'
                        className='me-2'
                        onClick={handleSaveClick}
                        disabled={
                          !formState.saveBtnEnabled
                          || lastAddedItemId === null
                          || (caseValidationType === EnumUtility.CaseType.Case &&
                            !String(formValues.narrativeForStateTxt || "").trim())
                          || (caseValidationType === EnumUtility.CaseType.NoCase &&
                            !String(formValues.noteForActionTxt || "").trim())}
                      >
                        Save
                      </Button>
                    )}
                  </div>
                </div>
              </footer>
            </div>
          </div>
        </div>
      </div >
    </div>
  );

  const rightPaneContent = (
    <div style={{ width: '100%', height: '100%' }}>
      <CaseInformationPane
        ref={caseInfoRef}
        onCaseSearchComplete={handleCaseSearchComplete}
        pageName={'Next Work Item'}
      // onMessageUpdate={handleShowBatchDocMessage}
      />
    </div>
  );


  return (
    <div>
      <ToggleSplitPane
        leftPaneVisibility={true}
        leftPaneTitle={leftPaneTitle}
        leftPaneContent={leftPaneContent}
        rightPaneTitle={rightPaneTitle}
        rightPaneContent={rightPaneContent}
        leftPaneSize='50%'
      >
      </ToggleSplitPane>

      <AlertNotification
        alertType={alertProps.type}
        message={alertProps.message}
        open={alertProps.open}
        onClose={handleAlertClose}
      />

      {/* Include the loader component */}
      <PageLoader isPageLoading={isPageLoading} />

      {/* Include the Confirmation Dialog component */}
      <ConfirmationDialog
        openConfirmation={confirmDialogOpen}
        dialogContent={confirmationDialogContent}
        onCloseConfirmation={handleCloseConfirmDialog}
        onConfirmDialog={(param) => handleDialogConfirmAction(param)}
      />

      <CommonDialog
        openDialog={dialogCommonState.openDialog}
        onCloseDialog={handleCloseCommonDialog}
        title={dialogCommonState.contentProps.Title}
        minHeightCustom={dialogCommonState.contentProps.minHeightCustom || 200}
        minWidthCustom={dialogCommonState.contentProps.minWidthCustom || 700}
        contentDialog={DialogContentManager[dialogCommonState.contentType]}
        contentProps={{
          contentType: dialogCommonState.contentType,
          ...dialogCommonState.contentProps
        }}
      />

      {/* Include the Notification Dialog component */}
      <NotificationDialog
        openNotification={notificationDialogOpen}
        dialogContent={notificationDialogContent}
        onCloseNotification={handleCloseNotificationDialog}
        isAutoClose={timerAutoCloseNotificationDialog}
        timerDuration={timerNotificationDialogClose}
        onConfirm={handleOkClick}
      />

       <CaseSearchDialog
        openDialogBox={caseSearchDialogOpen}
        onCloseDialog={() => {
          setCaseSearchDialogOpen(false);
          setCorrectCaseSearch('');
          setCorrectCaseSearchData(null);
          setCorrectCaseSearchComplete(false);
          setIndexCase(false);
        }}
        onCaseSearch={handleCorrectCaseSearch}
        onCaseChange={handleCorrectCaseChange}
        onCaseClear={ handleCorrectCaseSearchClear}
        caseNumber={correctCaseSearch}
        caseNumberRegex={caseNumberRegex}
        caseData={correctCaseSearchData}
        onApplyCase={handleApplyCaseClick}
        onProceedWithoutCase={handleProceedWithoutCaseClick}
        caseSearchComplete={correctCaseSearchComplete}
        isSupRequired={isSupRequired}
        stateID={getLoginStateID()}
        noCase = {noCase}
        maxWidth="md"
        minHeight={300}
        maxHeight={600}
      />

    </div>
  );
});

export default NextWorkitem;
